import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Download, RotateCcw, FileText, Eye, EyeOff, Plus, Trash2, Save, CheckCircle, List, X, ChevronDown } from 'lucide-react';
import { fetchProposalProducts, fetchProposalSettings, seedProposalCatalog } from '../actions/proposalCatalog';
import { createProposal, updateProposal } from '../actions/proposals';
import VisualLockerLine, { getModuleImage } from '../components/VisualLockerLine';
import {
  LICENSE_TYPES, MAINTENANCE_TYPES, ZONES, LOCKER_RANGES,
  INSTALLATION_TYPES, LOCK_TYPES, LOCK_ACCESS_TYPES, LOCK_CLOSE_TYPES, SALE_MODES, OPERATING_MODES, fmt, fmtAlways, fmtPct
} from '../utils/proposalCatalog';
import '../styles/ProposalConfiguratorView.css';

// ─── Design Tokens ─────────────────────────────────────────
const TEAL      = '#0a4080';
const DARK      = '#001d3a';
const BG        = '#f4f6f9';
const CARD      = '#ffffff';
const BORDER    = '#e2e8f0';
const TEXT_1    = '#1e293b';
const TEXT_2    = '#64748b';
const TEXT_3    = '#94a3b8';
const GREEN     = '#10b981';
const AMBER     = '#f59e0b';
const RED       = '#ef4444';

const TH_BG    = '#f0fdfa';
const TH_COLOR = '#0e7490';
const ROW_HL   = '#f0f9ff';

// ─── Shared inline style fragments ────────────────────────
const cardStyle    = { background: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, padding: 24 };
const labelStyle   = { fontSize: 12, fontWeight: 600, color: TEXT_2, marginBottom: 4, display: 'block' };
const inputStyle   = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, color: TEXT_1, outline: 'none', boxSizing: 'border-box' };
const selectStyle  = { ...inputStyle, background: '#fff', cursor: 'pointer' };
const sectionTitle = { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: TEXT_2, marginBottom: 16 };

// ─── PDF Translation Tables ────────────────────────────────
const PDF_T = {
  es: {
    coverLine1: 'OFERTA ', coverLine2: 'COMERCIAL',
    preparedBy: 'Realizado por', offerRef: 'Referencia de la oferta', date: 'Fecha',
    locale: 'es-ES',
    commercialProposal: 'PROPUESTA COMERCIAL',
    dateLabel: 'Fecha: ', companyLabel: 'Empresa: ', contactLabel: 'Contacto: ',
    acceptanceText: 'Para la aceptación del presupuesto, por favor devuelva este documento firmado y sellado con la siguiente información:',
    companyDetails: 'Datos de la Empresa',
    sigFields: [['Razón social'], ['CIF / NIF'], ['Dirección del domicilio social'], ['Nombre y apellidos']],
    companyStamp: 'Firma y sello de la empresa', dateSignature: 'Fecha: _______________',
    code: 'Código', description: 'Descripción', quantity: 'Cantidad', lockers: 'Taquillas',
    unitPrice: 'Precio Ud.', totalSale: 'Total Venta', subtotal: 'Subtotal',
    installationProposal: 'Propuesta de Instalación',
    cfgLabels: ['Módulos','Taquillas','Tipo Instalación','Tipo Cerradura','Acceso','Modo Funcionamiento','Venta Online','Tipo de Contrato','Licencia','Mantenimiento','Personalización'],
    yesVal: 'Sí', noVal: 'No', noLicense: 'Sin licencia',
    lockerCabinets: 'Armarios de taquillas', pointsOfSale: 'Puntos de venta', spareParts: 'Recambios',
    disc: 'Dto. ', total: 'Total',
    economicOffer: 'Oferta Económica y Condiciones',
    lockersLabel: 'Taquillas Smart VACWAYlockers (',
    sparePartsLbl: 'Recambios', consumables: 'Consumibles', customization: 'Personalización',
    installationLabel: 'Instalación, formación y puesta en marcha',
    licensesLabel: 'Licencias (', maintenanceLabel: 'Mantenimiento (',
    perYear: ' /año', perMonth: ' /mes',
    notIncluded: 'No incluida', pending: 'Pendiente', included: 'Incluido', includedF: 'Incluidas',
    rental: 'ALQUILER', basePrice: 'Precio base', discountApplied: 'Descuento global aplicado',
    annualRentalLabel: 'Alquiler anual VACWAYlockers (', monthlyRentalLabel: 'Alquiler mensual VACWAYlockers (',
    depositLabel: (n) => 'Depósito ' + n + ' meses',
    contractMandatory: 'Duración del contrato (compromiso obligatorio)',
    sparePartsRental: 'Recambios (incluidos en alquiler)', consumablesOneOff: 'Consumibles (cargo único)',
    contractDuration: 'Duración del contrato',
    commissionYear: (n) => 'Comisión Cliente Año ' + n,
    minAnnualCommission: 'Cláusula de comisiones anuales mínimas',
    licenses: 'Licencias', maintenance: 'Mantenimiento',
    conditions: '* Condiciones', notes: 'Observaciones',
    yearsUnit: ' años',
    saleMode: { purchase: 'Compra', rental: 'Alquiler', revenue_share: 'Revenue Share' },
    maintType: { ESTANDAR: 'ESTÁNDAR', PREMIUM: 'PREMIUM', NONE: 'Sin mantenimiento' },
    opMode: { '24H': 'Día', Tiempo: 'Tiempo', Apertura: 'Apertura', Paqueteria: 'Paquetería', PUBLIC: 'Pública' },
    licenseType: { BASIC: 'BASIC', PRO: 'PRO', NONE: 'Sin licencia' },
    filename: (ref, date) => 'VACWAY_Propuesta_' + ref + '_' + date + '.pdf',
  },
  en: {
    coverLine1: 'COMMERCIAL ', coverLine2: 'OFFER',
    preparedBy: 'Prepared by', offerRef: 'Offer Reference', date: 'Date',
    locale: 'en-GB',
    commercialProposal: 'COMMERCIAL PROPOSAL',
    dateLabel: 'Date: ', companyLabel: 'Company: ', contactLabel: 'Contact: ',
    acceptanceText: 'To accept this proposal, please return this document signed and stamped with the following information:',
    companyDetails: 'Company Details',
    sigFields: [['Company Name'], ['Tax ID / VAT Number'], ['Registered Address'], ['Full Name']],
    companyStamp: 'Company signature and stamp', dateSignature: 'Date: _______________',
    code: 'Code', description: 'Description', quantity: 'Quantity', lockers: 'Lockers',
    unitPrice: 'Unit Price', totalSale: 'Total Sale', subtotal: 'Subtotal',
    installationProposal: 'Installation Proposal',
    cfgLabels: ['Modules','Lockers','Installation Type','Lock Type','Access','Operating Mode','Online Sales','Contract Type','License','Maintenance','Customization'],
    yesVal: 'Yes', noVal: 'No', noLicense: 'No license',
    lockerCabinets: 'Locker Cabinets', pointsOfSale: 'Points of Sale', spareParts: 'Spare Parts',
    disc: 'Disc. ', total: 'Total',
    economicOffer: 'Economic Offer and Conditions',
    lockersLabel: 'Smart VACWAYlockers Lockers (',
    sparePartsLbl: 'Spare parts', consumables: 'Consumables', customization: 'Customization',
    installationLabel: 'Installation, training and commissioning',
    licensesLabel: 'Licenses (', maintenanceLabel: 'Maintenance (',
    perYear: ' /year', perMonth: ' /month',
    notIncluded: 'Not included', pending: 'Pending', included: 'Included', includedF: 'Included',
    rental: 'RENTAL', basePrice: 'Base price', discountApplied: 'Global discount applied',
    annualRentalLabel: 'Annual Rental VACWAYlockers (', monthlyRentalLabel: 'Monthly Rental VACWAYlockers (',
    depositLabel: (n) => n + '-Month Deposit',
    contractMandatory: 'Contract Duration (mandatory commitment)',
    sparePartsRental: 'Spare parts (included in rental)', consumablesOneOff: 'Consumables (one-off)',
    contractDuration: 'Contract Duration',
    commissionYear: (n) => 'Client Commission Year ' + n,
    minAnnualCommission: 'Minimum Annual Commission Clause',
    licenses: 'Licenses', maintenance: 'Maintenance',
    conditions: '* Conditions', notes: 'Notes',
    yearsUnit: ' year(s)',
    saleMode: { purchase: 'Purchase', rental: 'Rental', revenue_share: 'Revenue Share' },
    maintType: { ESTANDAR: 'STANDARD', PREMIUM: 'PREMIUM', NONE: 'No Maintenance' },
    opMode: { '24H': 'Day', Tiempo: 'Time', Apertura: 'Opening', Paqueteria: 'Parcel', PUBLIC: 'Public' },
    licenseType: { BASIC: 'BASIC', PRO: 'PRO', NONE: 'No License' },
    filename: (ref, date) => 'VACWAY_Proposal_' + ref + '_' + date + '.pdf',
  },
  it: {
    coverLine1: 'OFFERTA ', coverLine2: 'COMMERCIALE',
    preparedBy: 'Preparato da', offerRef: 'Riferimento offerta', date: 'Data',
    locale: 'it-IT',
    commercialProposal: 'PROPOSTA COMMERCIALE',
    dateLabel: 'Data: ', companyLabel: 'Azienda: ', contactLabel: 'Contatto: ',
    acceptanceText: "Per accettare questa proposta, si prega di restituire questo documento firmato e timbrato con le seguenti informazioni:",
    companyDetails: "Dati dell'Azienda",
    sigFields: [['Ragione sociale'], ['Codice Fiscale / P.IVA'], ['Sede legale'], ['Nome e cognome']],
    companyStamp: "Firma e timbro dell'azienda", dateSignature: 'Data: _______________',
    code: 'Codice', description: 'Descrizione', quantity: 'Quantità', lockers: 'Armadietti',
    unitPrice: 'Prezzo un.', totalSale: 'Totale vendita', subtotal: 'Subtotale',
    installationProposal: 'Proposta di installazione',
    cfgLabels: ['Moduli','Armadietti','Tipo installazione','Tipo di serratura','Accesso','Modalità operativa','Vendita online','Tipo di contratto','Licenza','Manutenzione','Personalizzazione'],
    yesVal: 'Sì', noVal: 'No', noLicense: 'Nessuna licenza',
    lockerCabinets: 'Armadi armadietti', pointsOfSale: 'Punti vendita', spareParts: 'Ricambi',
    disc: 'Sconto ', total: 'Totale',
    economicOffer: 'Offerta economica e condizioni',
    lockersLabel: 'Armadietti Smart VACWAYlockers (',
    sparePartsLbl: 'Ricambi', consumables: 'Materiali di consumo', customization: 'Personalizzazione',
    installationLabel: 'Installazione, formazione e messa in servizio',
    licensesLabel: 'Licenze (', maintenanceLabel: 'Manutenzione (',
    perYear: ' /anno', perMonth: ' /mese',
    notIncluded: 'Non inclusa', pending: 'In attesa', included: 'Inclusa', includedF: 'Incluse',
    rental: 'NOLEGGIO', basePrice: 'Prezzo base', discountApplied: 'Sconto globale applicato',
    annualRentalLabel: 'Noleggio annuale VACWAYlockers (', monthlyRentalLabel: 'Noleggio mensile VACWAYlockers (',
    depositLabel: (n) => 'Deposito ' + n + ' mesi',
    contractMandatory: 'Durata contratto (impegno obbligatorio)',
    sparePartsRental: 'Ricambi (inclusi nel noleggio)', consumablesOneOff: 'Materiali di consumo (addebito unico)',
    contractDuration: 'Durata contratto',
    commissionYear: (n) => 'Commissione cliente anno ' + n,
    minAnnualCommission: 'Clausola commissione minima annuale',
    licenses: 'Licenze', maintenance: 'Manutenzione',
    conditions: '* Condizioni', notes: 'Note',
    yearsUnit: ' anno/i',
    saleMode: { purchase: 'Acquisto', rental: 'Noleggio', revenue_share: 'Revenue Share' },
    maintType: { ESTANDAR: 'STANDARD', PREMIUM: 'PREMIUM', NONE: 'Nessuna manutenzione' },
    opMode: { '24H': 'Giorno', Tiempo: 'Tempo', Apertura: 'Apertura', Paqueteria: 'Pacco', PUBLIC: 'Pubblico' },
    licenseType: { BASIC: 'BASIC', PRO: 'PRO', NONE: 'Nessuna licenza' },
    filename: (ref, date) => 'VACWAY_Proposta_' + ref + '_' + date + '.pdf',
  },
  fr: {
    coverLine1: 'OFFRE ', coverLine2: 'COMMERCIALE',
    preparedBy: 'Préparé par', offerRef: "Référence de l'offre", date: 'Date',
    locale: 'fr-FR',
    commercialProposal: 'PROPOSITION COMMERCIALE',
    dateLabel: 'Date : ', companyLabel: 'Société : ', contactLabel: 'Contact : ',
    acceptanceText: "Pour accepter cette proposition, veuillez retourner ce document signé et tamponné avec les informations suivantes :",
    companyDetails: "Données de l'entreprise",
    sigFields: [['Raison sociale'], ["N° SIREN / TVA"], ['Adresse du siège social'], ['Nom et prénom']],
    companyStamp: "Signature et cachet de l'entreprise", dateSignature: 'Date : _______________',
    code: 'Code', description: 'Description', quantity: 'Quantité', lockers: 'Casiers',
    unitPrice: 'Prix unit.', totalSale: 'Total vente', subtotal: 'Sous-total',
    installationProposal: "Proposition d'installation",
    cfgLabels: ['Modules','Casiers',"Type d'installation",'Type de serrure','Accès','Mode opératoire','Vente en ligne','Type de contrat','Licence','Maintenance','Personnalisation'],
    yesVal: 'Oui', noVal: 'Non', noLicense: 'Sans licence',
    lockerCabinets: 'Armoires à casiers', pointsOfSale: 'Points de vente', spareParts: 'Pièces détachées',
    disc: 'Rem. ', total: 'Total',
    economicOffer: 'Offre économique et conditions',
    lockersLabel: 'Casiers Smart VACWAYlockers (',
    sparePartsLbl: 'Pièces détachées', consumables: 'Consommables', customization: 'Personnalisation',
    installationLabel: 'Installation, formation et mise en service',
    licensesLabel: 'Licences (', maintenanceLabel: 'Maintenance (',
    perYear: ' /an', perMonth: ' /mois',
    notIncluded: 'Non incluse', pending: 'En attente', included: 'Incluse', includedF: 'Incluses',
    rental: 'LOCATION', basePrice: 'Prix de base', discountApplied: 'Remise globale appliquée',
    annualRentalLabel: 'Location annuelle VACWAYlockers (', monthlyRentalLabel: 'Location mensuelle VACWAYlockers (',
    depositLabel: (n) => 'Dépôt de garantie ' + n + ' mois',
    contractMandatory: 'Durée du contrat (engagement obligatoire)',
    sparePartsRental: 'Pièces détachées (incluses dans la location)', consumablesOneOff: 'Consommables (frais unique)',
    contractDuration: 'Durée du contrat',
    commissionYear: (n) => 'Commission client année ' + n,
    minAnnualCommission: 'Clause de commission annuelle minimale',
    licenses: 'Licences', maintenance: 'Maintenance',
    conditions: '* Conditions', notes: 'Observations',
    yearsUnit: ' an(s)',
    saleMode: { purchase: 'Achat', rental: 'Location', revenue_share: 'Partage des revenus' },
    maintType: { ESTANDAR: 'STANDARD', PREMIUM: 'PREMIUM', NONE: 'Sans maintenance' },
    opMode: { '24H': 'Journée', Tiempo: 'Temps', Apertura: 'Ouverture', Paqueteria: 'Colis', PUBLIC: 'Public' },
    licenseType: { BASIC: 'BASIC', PRO: 'PRO', NONE: 'Sans licence' },
    filename: (ref, date) => 'VACWAY_Proposition_' + ref + '_' + date + '.pdf',
  },
  de: {
    coverLine1: 'KOMMERZIELLES ', coverLine2: 'ANGEBOT',
    preparedBy: 'Erstellt von', offerRef: 'Angebotsreferenz', date: 'Datum',
    locale: 'de-DE',
    commercialProposal: 'KOMMERZIELLES ANGEBOT',
    dateLabel: 'Datum: ', companyLabel: 'Unternehmen: ', contactLabel: 'Kontakt: ',
    acceptanceText: 'Um dieses Angebot anzunehmen, senden Sie dieses Dokument bitte unterzeichnet und gestempelt mit den folgenden Informationen zurück:',
    companyDetails: 'Unternehmensdaten',
    sigFields: [['Firmenname'], ['Steuernummer / USt-IdNr.'], ['Geschäftsadresse'], ['Vor- und Nachname']],
    companyStamp: 'Unterschrift und Stempel des Unternehmens', dateSignature: 'Datum: _______________',
    code: 'Code', description: 'Beschreibung', quantity: 'Menge', lockers: 'Schließfächer',
    unitPrice: 'Einheitspreis', totalSale: 'Gesamtverkauf', subtotal: 'Zwischensumme',
    installationProposal: 'Installationsvorschlag',
    cfgLabels: ['Module','Schließfächer','Installationstyp','Schlosstyp','Zugang','Betriebsmodus','Online-Verkauf','Vertragstyp','Lizenz','Wartung','Anpassung'],
    yesVal: 'Ja', noVal: 'Nein', noLicense: 'Keine Lizenz',
    lockerCabinets: 'Schließfachschränke', pointsOfSale: 'Verkaufspunkte', spareParts: 'Ersatzteile',
    disc: 'Rabatt ', total: 'Gesamt',
    economicOffer: 'Wirtschaftliches Angebot und Bedingungen',
    lockersLabel: 'Smart VACWAYlockers Schließfächer (',
    sparePartsLbl: 'Ersatzteile', consumables: 'Verbrauchsmaterialien', customization: 'Anpassung',
    installationLabel: 'Installation, Schulung und Inbetriebnahme',
    licensesLabel: 'Lizenzen (', maintenanceLabel: 'Wartung (',
    perYear: ' /Jahr', perMonth: ' /Monat',
    notIncluded: 'Nicht enthalten', pending: 'Ausstehend', included: 'Enthalten', includedF: 'Enthalten',
    rental: 'MIETE', basePrice: 'Grundpreis', discountApplied: 'Globaler Rabatt angewendet',
    annualRentalLabel: 'Jährliche Miete VACWAYlockers (', monthlyRentalLabel: 'Monatliche Miete VACWAYlockers (',
    depositLabel: (n) => 'Kaution ' + n + ' Monate',
    contractMandatory: 'Vertragslaufzeit (Pflichtbindung)',
    sparePartsRental: 'Ersatzteile (in Miete enthalten)', consumablesOneOff: 'Verbrauchsmaterialien (einmalige Gebühr)',
    contractDuration: 'Vertragslaufzeit',
    commissionYear: (n) => 'Kundenprovision Jahr ' + n,
    minAnnualCommission: 'Mindestjahresprovisions-Klausel',
    licenses: 'Lizenzen', maintenance: 'Wartung',
    conditions: '* Bedingungen', notes: 'Anmerkungen',
    yearsUnit: ' Jahr(e)',
    saleMode: { purchase: 'Kauf', rental: 'Miete', revenue_share: 'Umsatzbeteiligung' },
    maintType: { ESTANDAR: 'STANDARD', PREMIUM: 'PREMIUM', NONE: 'Keine Wartung' },
    opMode: { '24H': 'Tag', Tiempo: 'Zeit', Apertura: 'Öffnung', Paqueteria: 'Paket', PUBLIC: 'Öffentlich' },
    licenseType: { BASIC: 'BASIC', PRO: 'PRO', NONE: 'Keine Lizenz' },
    filename: (ref, date) => 'VACWAY_Angebot_' + ref + '_' + date + '.pdf',
  },
  pt: {
    coverLine1: 'OFERTA ', coverLine2: 'COMERCIAL',
    preparedBy: 'Preparado por', offerRef: 'Referência da oferta', date: 'Data',
    locale: 'pt-PT',
    commercialProposal: 'PROPOSTA COMERCIAL',
    dateLabel: 'Data: ', companyLabel: 'Empresa: ', contactLabel: 'Contacto: ',
    acceptanceText: 'Para aceitar esta proposta, por favor devolva este documento assinado e carimbado com as seguintes informações:',
    companyDetails: 'Dados da Empresa',
    sigFields: [['Razão social'], ['NIF / NIPC'], ['Sede social'], ['Nome completo']],
    companyStamp: 'Assinatura e carimbo da empresa', dateSignature: 'Data: _______________',
    code: 'Código', description: 'Descrição', quantity: 'Quantidade', lockers: 'Cacifos',
    unitPrice: 'Preço un.', totalSale: 'Total venda', subtotal: 'Subtotal',
    installationProposal: 'Proposta de instalação',
    cfgLabels: ['Módulos','Cacifos','Tipo de instalação','Tipo de fechadura','Acesso','Modo operacional','Venda online','Tipo de contrato','Licença','Manutenção','Personalização'],
    yesVal: 'Sim', noVal: 'Não', noLicense: 'Sem licença',
    lockerCabinets: 'Armários de cacifos', pointsOfSale: 'Pontos de venda', spareParts: 'Peças sobressalentes',
    disc: 'Desc. ', total: 'Total',
    economicOffer: 'Oferta económica e condições',
    lockersLabel: 'Cacifos Smart VACWAYlockers (',
    sparePartsLbl: 'Peças sobressalentes', consumables: 'Consumíveis', customization: 'Personalização',
    installationLabel: 'Instalação, formação e colocação em funcionamento',
    licensesLabel: 'Licenças (', maintenanceLabel: 'Manutenção (',
    perYear: ' /ano', perMonth: ' /mês',
    notIncluded: 'Não incluída', pending: 'Pendente', included: 'Incluída', includedF: 'Incluídas',
    rental: 'ALUGUER', basePrice: 'Preço base', discountApplied: 'Desconto global aplicado',
    annualRentalLabel: 'Aluguer anual VACWAYlockers (', monthlyRentalLabel: 'Aluguer mensal VACWAYlockers (',
    depositLabel: (n) => 'Depósito ' + n + ' meses',
    contractMandatory: 'Duração do contrato (compromisso obrigatório)',
    sparePartsRental: 'Peças sobressalentes (incluídas no aluguer)', consumablesOneOff: 'Consumíveis (encargo único)',
    contractDuration: 'Duração do contrato',
    commissionYear: (n) => 'Comissão do cliente ano ' + n,
    minAnnualCommission: 'Cláusula de comissão anual mínima',
    licenses: 'Licenças', maintenance: 'Manutenção',
    conditions: '* Condições', notes: 'Observações',
    yearsUnit: ' ano(s)',
    saleMode: { purchase: 'Compra', rental: 'Aluguer', revenue_share: 'Partilha de receitas' },
    maintType: { ESTANDAR: 'STANDARD', PREMIUM: 'PREMIUM', NONE: 'Sem manutenção' },
    opMode: { '24H': 'Dia', Tiempo: 'Tempo', Apertura: 'Abertura', Paqueteria: 'Encomendas', PUBLIC: 'Público' },
    licenseType: { BASIC: 'BASIC', PRO: 'PRO', NONE: 'Sem licença' },
    filename: (ref, date) => 'VACWAY_Proposta_' + ref + '_' + date + '.pdf',
  },
};

// ─── Revenue-Share Study Translation Table ─────────────────
const RS_T = {
  es: {
    title: 'Estudio de Revenue-Share', subtitle: 'VACWAYlockers — Formulario de recopilación de datos del cliente',
    lockerConfig: 'Configuración de taquillas', type1: 'TIPO 1', type2: 'TIPO 2', total: 'TOTAL',
    qtyLockers: 'CANTIDAD DE TAQUILLAS', lockersSize: 'TAMAÑO TAQUILLAS [CM]', lockersPrice: 'PRECIO TAQUILLAS [€]',
    note: '* Rellene el Tipo 2 solo si existen dos tamaños de taquilla diferentes en la instalación.',
    lockersRevenue: 'Ingresos por taquillas', month: 'MES', parkAttendance: 'Afluencia del parque',
    signatureStamp: 'Firma y sello', sigFields: ['Empresa', 'Nombre', 'Departamento', 'Fecha'], stampArea: 'Espacio para sello',
    months: ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'],
    filename: (ref, date) => 'VACWAY_Estudio_RS_' + ref + '_' + date + '.pdf',
  },
  en: {
    title: 'Revenue-Share Study', subtitle: 'VACWAYlockers — Customer Data Collection Form',
    lockerConfig: 'Locker Configuration', type1: 'TYPE 1', type2: 'TYPE 2', total: 'TOTAL',
    qtyLockers: 'QTY OF LOCKERS', lockersSize: 'LOCKERS SIZE [CM]', lockersPrice: 'LOCKERS PRICE [€]',
    note: '* Fill in Type 2 only if two different locker sizes exist in the installation.',
    lockersRevenue: 'Lockers Revenue', month: 'MONTH', parkAttendance: 'Park Attendance',
    signatureStamp: 'Signature & Stamp', sigFields: ['Company', 'Name', 'Department', 'Date'], stampArea: 'Stamp area',
    months: ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'],
    filename: (ref, date) => 'VACWAY_RS_Study_' + ref + '_' + date + '.pdf',
  },
  it: {
    title: 'Studio Revenue-Share', subtitle: 'VACWAYlockers — Modulo di raccolta dati cliente',
    lockerConfig: 'Configurazione armadietti', type1: 'TIPO 1', type2: 'TIPO 2', total: 'TOTALE',
    qtyLockers: 'QUANTITÀ ARMADIETTI', lockersSize: 'DIMENSIONE ARMADIETTI [CM]', lockersPrice: 'PREZZO ARMADIETTI [€]',
    note: "* Compilare il Tipo 2 solo se nell'installazione esistono due dimensioni di armadietti diverse.",
    lockersRevenue: 'Ricavi armadietti', month: 'MESE', parkAttendance: 'Affluenza del parco',
    signatureStamp: 'Firma e timbro', sigFields: ['Azienda', 'Nome', 'Reparto', 'Data'], stampArea: 'Area timbro',
    months: ['GENNAIO','FEBBRAIO','MARZO','APRILE','MAGGIO','GIUGNO','LUGLIO','AGOSTO','SETTEMBRE','OTTOBRE','NOVEMBRE','DICEMBRE'],
    filename: (ref, date) => 'VACWAY_Studio_RS_' + ref + '_' + date + '.pdf',
  },
  fr: {
    title: 'Étude de Revenue-Share', subtitle: 'VACWAYlockers — Formulaire de collecte de données client',
    lockerConfig: 'Configuration des casiers', type1: 'TYPE 1', type2: 'TYPE 2', total: 'TOTAL',
    qtyLockers: 'QUANTITÉ DE CASIERS', lockersSize: 'TAILLE CASIERS [CM]', lockersPrice: 'PRIX CASIERS [€]',
    note: "* Remplissez le Type 2 uniquement si deux tailles de casiers différentes existent dans l'installation.",
    lockersRevenue: 'Revenus des casiers', month: 'MOIS', parkAttendance: 'Fréquentation du parc',
    signatureStamp: 'Signature et cachet', sigFields: ['Société', 'Nom', 'Département', 'Date'], stampArea: 'Zone du cachet',
    months: ['JANVIER','FÉVRIER','MARS','AVRIL','MAI','JUIN','JUILLET','AOÛT','SEPTEMBRE','OCTOBRE','NOVEMBRE','DÉCEMBRE'],
    filename: (ref, date) => 'VACWAY_Etude_RS_' + ref + '_' + date + '.pdf',
  },
  de: {
    title: 'Revenue-Share-Studie', subtitle: 'VACWAYlockers — Formular zur Kundendatenerfassung',
    lockerConfig: 'Schließfach-Konfiguration', type1: 'TYP 1', type2: 'TYP 2', total: 'GESAMT',
    qtyLockers: 'ANZAHL SCHLIESSFÄCHER', lockersSize: 'SCHLIESSFACHGRÖSSE [CM]', lockersPrice: 'SCHLIESSFACHPREIS [€]',
    note: '* Füllen Sie Typ 2 nur aus, wenn in der Installation zwei verschiedene Schließfachgrößen vorhanden sind.',
    lockersRevenue: 'Schließfach-Einnahmen', month: 'MONAT', parkAttendance: 'Park-Besucherzahl',
    signatureStamp: 'Unterschrift & Stempel', sigFields: ['Firma', 'Name', 'Abteilung', 'Datum'], stampArea: 'Stempelbereich',
    months: ['JANUAR','FEBRUAR','MÄRZ','APRIL','MAI','JUNI','JULI','AUGUST','SEPTEMBER','OKTOBER','NOVEMBER','DEZEMBER'],
    filename: (ref, date) => 'VACWAY_RS_Studie_' + ref + '_' + date + '.pdf',
  },
  pt: {
    title: 'Estudo de Revenue-Share', subtitle: 'VACWAYlockers — Formulário de recolha de dados do cliente',
    lockerConfig: 'Configuração de cacifos', type1: 'TIPO 1', type2: 'TIPO 2', total: 'TOTAL',
    qtyLockers: 'QUANTIDADE DE CACIFOS', lockersSize: 'TAMANHO CACIFOS [CM]', lockersPrice: 'PREÇO CACIFOS [€]',
    note: '* Preencha o Tipo 2 apenas se existirem dois tamanhos de cacifos diferentes na instalação.',
    lockersRevenue: 'Receita de cacifos', month: 'MÊS', parkAttendance: 'Afluência do parque',
    signatureStamp: 'Assinatura e carimbo', sigFields: ['Empresa', 'Nome', 'Departamento', 'Data'], stampArea: 'Área do carimbo',
    months: ['JANEIRO','FEVEREIRO','MARÇO','ABRIL','MAIO','JUNHO','JULHO','AGOSTO','SETEMBRO','OUTUBRO','NOVEMBRO','DEZEMBRO'],
    filename: (ref, date) => 'VACWAY_Estudo_RS_' + ref + '_' + date + '.pdf',
  },
};

const LANGUAGES = [
  { code: 'es', imageId: 'es', label: 'Español' },
  { code: 'en', imageId: 'en', label: 'English' },
  { code: 'it', imageId: 'it', label: 'Italiano' },
  { code: 'fr', imageId: 'fr', label: 'Français' },
  { code: 'de', imageId: 'gr', label: 'Deutsch' },
  { code: 'pt', imageId: 'pt', label: 'Português' },
];

// Comercial Anexo PDF (hosted on S3) — suffix follows the selected export language
const ANEXO_BASE_URL = 'https://vacway-comercial.s3.eu-central-1.amazonaws.com/VW_ANEXO_COMERCIAL_2026_';
const anexoUrl = (lang) => `${ANEXO_BASE_URL}${(lang || 'es').toUpperCase()}.pdf`;

// Decimal-aware formatter for fractional prices (consumables)
const fmtDec = (n) => {
  if (n == null || isNaN(n)) return '0,00 €';
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

// ─── Component ─────────────────────────────────────────────
class ProposalConfiguratorView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Client info
      clientName: '',
      clientCompany: '',
      clientCIF: '',
      clientAddress: '',
      proposalRef: '',
      proposalDate: new Date().toISOString().slice(0, 10),
      notes: '',
      // Configuration
      licenseType: 'BASIC',
      maintenanceType: 'ESTANDAR',
      zone: 'ZONA_I',
      lockerRange: '0-500',
      customization: false,
      installationType: 'interior',
      catalogTab: 'interior',
      lockType: 'BLUETOOTH',
      pinRfidType: 'PIN',
      lockCloseType: 'HOOK',
      saleMode: 'purchase',
      operatingMode: '24H',
      onlineSale: false,
      // Rental / Revenue Share fields
      contractDuration: 60,
      depositMonths: 4,
      commissionPcts: [],
      hideMinAnnualCommission: false,
      salesYear1: '',
      salesYear2: '',
      salesYear3: '',
      existingLockers: '',
      installationCost: 'Pendiente',
      conditions: '',
      // Discounts
      discountProduct: 0,
      discountMaintenance: 0,
      discountLicense: 0,
      discountRental: 0,
      // Added line items: [ { productId, code, name, lockers, unitPrice, unitRent, qty } ]
      cabinetItems: [],
      posItems: [],
      spareItems: [],
      // Consumables: [ { productId, code, name, image, unitPrice, lockers, qty } ]
      consumableItems: [],
      // Visual configurator line
      configLine: [],
      // Module input mode: 'visual' | 'list'
      moduleMode: 'visual',
      // Selector state (legacy – used by inline selects, kept for back-compat)
      selectedCabinetCode: '',
      selectedPosCode: '',
      selectedConsumableCode: '',
      // Product picker modal: null = closed, or category string
      addProductModal: null,
      addProductModalSearch: '',
      selectedSpareCode: '',
      // PDF / preview
      generatingPdf: false,
      generatingRsStudy: false,
      exportLang: 'es',
      langDropdownOpen: false,
      showPreview: false,
      // Save state
      savedProposalId: null,
      savingProposal: false,
      saveSuccess: false,
      // ── Rentability analysis block ──
      rentab_model: 'sale',
    };
    this.printRef = React.createRef();
    this.printCoverRef = React.createRef();
    this.printVisualRef = React.createRef();
    this.printDataRef = React.createRef();
    this.printOfertaRef = React.createRef();
    this.printRsStudyRef = React.createRef();
  }

  componentDidMount() {
    this.props.fetchProposalProducts();
    this.props.fetchProposalSettings();
    // Load saved proposal if navigated from ProposalsView
    const loaded = this.props.location && this.props.location.state && this.props.location.state.loadedProposal;
    if (loaded) {
      this.loadProposal(loaded);
    }
  }

  componentDidUpdate(prevProps) {
    // Auto-seed if no products loaded
    if (this.props.products && this.props.products.length === 0 && prevProps.products === null) {
      this.props.seedProposalCatalog().then(() => this.props.fetchProposalProducts());
    }
    // Sync consumable unitPrices with the live catalog whenever products are refreshed
    if (this.props.products && this.props.products !== prevProps.products) {
      this.setState(prev => {
        if (!prev.consumableItems || prev.consumableItems.length === 0) return null;
        const updated = prev.consumableItems.map(it => {
          const product = this.props.products.find(p => p.code === it.code);
          return product ? { ...it, unitPrice: product.priceInterior || 0 } : it;
        });
        const changed = updated.some((it, i) => it.unitPrice !== prev.consumableItems[i].unitPrice);
        return changed ? { consumableItems: updated } : null;
      });
    }
  }

  // ─── Helpers: get DB-sourced data ──────────────────────
  getProducts(category) {
    const { products } = this.props;
    if (!products) return [];
    return products.filter(p => p.category === category);
  }

  getSetting(path, fallback) {
    const { settings } = this.props;
    if (!settings) return fallback;
    const parts = path.split('.');
    let v = settings;
    for (const k of parts) {
      if (v == null) return fallback;
      v = v[k];
    }
    return v ?? fallback;
  }

  getLicensePricePerPOS() {
    const { licenseType } = this.state;
    if (licenseType === 'NONE') return 0;
    return this.getSetting(`licensePricing.${licenseType}.perPOS`, licenseType === 'PRO' ? 135 : 99);
  }

  getLockerRangeKey(totalLockers) {
    if (totalLockers <= 500) return '0-500';
    if (totalLockers <= 1000) return '501-1000';
    return '1001-2000';
  }

  getMaintenanceCost(totalLockers) {
    const { maintenanceType, zone } = this.state;
    if (maintenanceType === 'NONE') return 0;
    const rangeKey   = this.getLockerRangeKey(totalLockers);
    const baseAnnual = this.getSetting(`maintenancePricing.${maintenanceType}.${zone}.${rangeKey}`, 0);
    const lockerStd  = this.getSetting('licensePricing.lockerStd', 0);
    return baseAnnual + lockerStd * totalLockers;
  }

  priceField(product) {
    return this.state.installationType === 'exterior' ? product.priceExterior : product.priceInterior;
  }

  rentField(product) {
    return this.state.installationType === 'exterior' ? product.rentExterior : product.rentInterior;
  }

  getConsumableUnitPrice(item) {
    const product = (this.props.products || []).find(p => p.code === item.code);
    return product ? (product.priceInterior || 0) : (item.unitPrice || 0);
  }

  // ─── Add / Remove Items ────────────────────────────────
  // ─── Build item object from a product ─────────────────
  _buildItem = (product) => {
    const isExt = this.state.installationType === 'exterior';
    return {
      productId: product._id,
      code: product.code,
      envCode: isExt ? (product.codeOutdoor || product.code) : (product.codeIndoor || product.code),
      name: product.name,
      lockers: product.lockers || 0,
      unitPrice: this.priceField(product),
      unitRent: this.rentField(product),
      personalizationPrice: product.personalizationPrice || 0,
      qty: 1,
    };
  };

  addItem = (category) => {
    const selKey = category === 'cabinet' ? 'selectedCabinetCode' : category === 'pos' ? 'selectedPosCode' : null;
    const code = selKey ? this.state[selKey] : null;
    if (!code) return;
    const product = (this.props.products || []).find(p => p.code === code);
    if (!product) return;
    this.addItemDirect(category, product);
    if (selKey) this.setState({ [selKey]: '' });
  };

  // Add directly from modal (no selector state dependency)
  addItemDirect = (category, product) => {
    const item = this._buildItem(product);
    if (category === 'cabinet') {
      this.setState(prev => ({ cabinetItems: [...prev.cabinetItems, item] }));
    } else if (category === 'pos') {
      this.setState(prev => ({ posItems: [...prev.posItems, item] }));
    } else if (category === 'spare') {
      this.setState(prev => ({ spareItems: [...prev.spareItems, item] }));
    }
  };

  removeItem = (category, index) => {
    if (category === 'cabinet') {
      this.setState(prev => ({ cabinetItems: prev.cabinetItems.filter((_, i) => i !== index) }));
    } else if (category === 'pos') {
      this.setState(prev => ({ posItems: prev.posItems.filter((_, i) => i !== index) }));
    } else if (category === 'spare') {
      this.setState(prev => ({ spareItems: prev.spareItems.filter((_, i) => i !== index) }));
    }
  };

  setItemQty = (category, index) => (e) => {
    const v = Math.max(1, parseInt(e.target.value) || 1);
    const key = category === 'cabinet' ? 'cabinetItems' : category === 'pos' ? 'posItems' : 'spareItems';
    this.setState(prev => {
      const items = [...prev[key]];
      items[index] = { ...items[index], qty: v };
      return { [key]: items };
    });
  };

  addConsumable = () => {
    const { selectedConsumableCode } = this.state;
    if (!selectedConsumableCode) return;
    const product = (this.props.products || []).find(p => p.code === selectedConsumableCode);
    if (!product) return;
    this.addConsumableDirect(product);
    this.setState({ selectedConsumableCode: '' });
  };

  // Add consumable directly from modal
  addConsumableDirect = (product) => {
    const item = {
      productId: product._id,
      code: product.code,
      name: product.name,
      image: product.image || product._image || '',
      unitPrice: product.priceInterior || 0,
      lockers: product.lockers || 0,
      qty: 1,
    };
    this.setState(prev => ({ consumableItems: [...prev.consumableItems, item] }));
  };

  removeConsumable = (index) => {
    this.setState(prev => ({ consumableItems: prev.consumableItems.filter((_, i) => i !== index) }));
  };

  setConsumableQty = (index) => (e) => {
    const v = Math.max(1, parseInt(e.target.value) || 1);
    this.setState(prev => {
      const items = [...prev.consumableItems];
      items[index] = { ...items[index], qty: v };
      return { consumableItems: items };
    });
  };

  // Sync visual line → cabinetItems + posItems
  onVisualLineChange = (line) => {
    const { products } = this.props;
    const isExt = this.state.installationType === 'exterior';
    const counts = {};
    line.forEach(m => { counts[m.code] = (counts[m.code] || 0) + 1; });
    const buildItems = (category) => Object.entries(counts).map(([code, qty]) => {
      const prod = (products || []).find(p => p.code === code && p.category === category);
      if (!prod) return null;
      return {
        productId: prod._id, code, envCode: isExt ? (prod.codeOutdoor || code) : (prod.codeIndoor || code),
        name: prod.name, lockers: prod.lockers, category: prod.category,
        unitPrice: isExt ? prod.priceExterior : prod.priceInterior,
        unitRent:  isExt ? prod.rentExterior  : prod.rentInterior,
        personalizationPrice: prod.personalizationPrice || 0,
        qty,
      };
    }).filter(Boolean);
    this.setState({ configLine: line, cabinetItems: buildItems('cabinet'), posItems: buildItems('pos') });
  };

  // When env tab changes
  handleInstallationChange = (tabValue) => {
    // Switching to POS tab: only change catalog view, never touch installationType
    if (tabValue === 'pos') {
      this.setState({ catalogTab: 'pos' });
      return;
    }

    // Switching between interior / exterior
    const installationType = tabValue;
    const isExt = installationType === 'exterior';
    const products = this.props.products || [];

    const rePriceItems = (items) => items.map(item => {
      const prod = products.find(p => p.code === item.code);
      if (!prod) return item;
      return { ...item, envCode: isExt ? (prod.codeOutdoor || item.code) : (prod.codeIndoor || item.code), unitPrice: isExt ? prod.priceExterior : prod.priceInterior, unitRent: isExt ? prod.rentExterior : prod.rentInterior };
    });

    // Re-price all items from visual line with new env pricing
    const { configLine } = this.state;
    const counts = {};
    configLine.forEach(m => { counts[m.code] = (counts[m.code] || 0) + 1; });
    const buildItems = (category) => Object.entries(counts).map(([code, qty]) => {
      const prod = products.find(p => p.code === code && p.category === category);
      if (!prod) return null;
      return {
        productId: prod._id, code, envCode: isExt ? (prod.codeOutdoor || code) : (prod.codeIndoor || code),
        name: prod.name, lockers: prod.lockers, category: prod.category,
        unitPrice: isExt ? prod.priceExterior : prod.priceInterior,
        unitRent:  isExt ? prod.rentExterior  : prod.rentInterior,
        personalizationPrice: prod.personalizationPrice || 0,
        qty,
      };
    }).filter(Boolean);

    this.setState(prev => ({
      installationType,
      catalogTab: tabValue,
      cabinetItems: configLine.length > 0 ? buildItems('cabinet') : rePriceItems(prev.cabinetItems),
      posItems:     configLine.length > 0 ? buildItems('pos')     : rePriceItems(prev.posItems),
      selectedPosCode: '',
    }));
  };

  // ─── Load saved proposal into form ───────────────────
  loadProposal = (p) => {
    this.setState({
      savedProposalId: p._id || null,
      clientName:    p.clientName    || '',
      clientCompany: p.clientCompany || '',
      clientCIF:     p.clientCIF     || '',
      clientAddress: p.clientAddress || '',
      proposalRef:   p.proposalRef   || '',
      proposalDate:  p.proposalDate  || new Date().toISOString().slice(0, 10),
      notes:         p.notes         || '',
      conditions:    p.conditions    || '',
      licenseType:     p.licenseType     || 'PRO',
      maintenanceType: p.maintenanceType || 'ESTANDAR',
      zone:            p.zone            || 'ZONA_II',
      lockerRange:     p.lockerRange     || '0-500',
      customization:   p.customization !== undefined ? p.customization : true,
      installationType: p.installationType || 'interior',
      catalogTab:      p.installationType || 'interior',
      lockType:        p.lockType   || 'STANDARD',
      pinRfidType:     p.pinRfidType || 'PIN',
      lockCloseType:   p.lockCloseType || 'HOOK',
      saleMode:        p.saleMode   || 'purchase',
      operatingMode:   p.operatingMode || '24H',
      onlineSale:      p.onlineSale !== undefined ? p.onlineSale : false,
      moduleMode:      p.moduleMode || 'visual',
      contractDuration:    p.contractDuration    !== undefined ? p.contractDuration    : 60,
      depositMonths:       p.depositMonths       !== undefined ? p.depositMonths       : 4,
      commissionPcts: Array.isArray(p.commissionPcts) ? p.commissionPcts
        : Array.from({ length: Math.round((p.contractDuration || 60) / 12) }, () => p.commissionPct || 0),
      hideMinAnnualCommission: p.hideMinAnnualCommission !== undefined ? p.hideMinAnnualCommission : false,
      salesYear1: p.salesYear1 != null ? p.salesYear1 : '',
      salesYear2: p.salesYear2 != null ? p.salesYear2 : '',
      salesYear3: p.salesYear3 != null ? p.salesYear3 : '',
      existingLockers: p.existingLockers != null ? p.existingLockers : '',
      installationCost:    p.installationCost != null ? p.installationCost : 'Pendiente',
      discountProduct:     p.discountProduct     !== undefined ? p.discountProduct     : 0,
      discountMaintenance: p.discountMaintenance !== undefined ? p.discountMaintenance : 0,
      discountLicense:     p.discountLicense     !== undefined ? p.discountLicense     : 0,
      discountRental:      p.discountRental      !== undefined ? p.discountRental      : 0,
      cabinetItems: p.cabinetItems || [],
      posItems:     p.posItems     || [],
      spareItems:   p.spareItems   || [],
      consumableItems: p.consumableItems || [],
      configLine:   p.configLine   || [],
    });
  };

  // ─── Collect current form state as proposal payload ───
  gatherProposalData = () => {
    const s = this.state;
    return {
      clientName:    s.clientName,
      clientCompany: s.clientCompany,
      clientCIF:     s.clientCIF,
      clientAddress: s.clientAddress,
      proposalRef:   s.proposalRef,
      proposalDate:  s.proposalDate,
      notes:         s.notes,
      conditions:    s.conditions,
      licenseType:     s.licenseType,
      maintenanceType: s.maintenanceType,
      zone:            s.zone,
      lockerRange:     s.lockerRange,
      customization:   s.customization,
      installationType: s.installationType,
      lockType:        s.lockType,
      pinRfidType:     s.pinRfidType,
      lockCloseType:   s.lockCloseType,
      saleMode:        s.saleMode,
      operatingMode:   s.operatingMode,
      onlineSale:      s.onlineSale,
      moduleMode:      s.moduleMode,
      contractDuration:    s.contractDuration,
      depositMonths:       s.depositMonths,
      commissionPcts: s.commissionPcts,
      hideMinAnnualCommission: s.hideMinAnnualCommission,
      salesYear1: s.salesYear1 !== '' ? (parseFloat(s.salesYear1) || 0) : null,
      salesYear2: s.salesYear2 !== '' ? (parseFloat(s.salesYear2) || 0) : null,
      salesYear3: s.salesYear3 !== '' ? (parseFloat(s.salesYear3) || 0) : null,
      existingLockers: s.existingLockers !== '' ? (parseInt(s.existingLockers) || 0) : null,
      installationCost:    typeof s.installationCost === 'number' ? s.installationCost : null,
      discountProduct:     s.discountProduct,
      discountMaintenance: s.discountMaintenance,
      discountLicense:     s.discountLicense,
      discountRental:      s.discountRental,
      cabinetItems: s.cabinetItems,
      posItems:     s.posItems,
      spareItems:   s.spareItems,
      consumableItems: s.consumableItems,
      configLine:   s.configLine,
      createdByName: this.props.fullname || '',
      createdById:   this.props.authId   || '',
    };
  };

  // ─── Save proposal ────────────────────────────────────
  saveProposal = async () => {
    const { savedProposalId } = this.state;
    this.setState({ savingProposal: true, saveSuccess: false });
    try {
      const data = this.gatherProposalData();
      if (savedProposalId) {
        const result = await this.props.updateProposal(savedProposalId, data);
        // Keep location.state in sync so a page refresh loads the latest saved data
        if (result && result.response) {
          this.props.history.replace(this.props.location.pathname, { loadedProposal: result.response });
        }
      } else {
        const result = await this.props.createProposal(data);
        // store the returned _id so subsequent saves become updates
        if (result && result.response && result.response._id) {
          this.setState({ savedProposalId: result.response._id });
          // Seed location.state so a page refresh loads the saved data
          this.props.history.replace(this.props.location.pathname, { loadedProposal: result.response });
        }
      }
      this.setState({ saveSuccess: true });
      setTimeout(() => this.setState({ saveSuccess: false }), 3000);
    } catch (err) {
      console.error('Error saving proposal:', err);
    } finally {
      this.setState({ savingProposal: false });
    }
  };

  // ─── Generic handlers ──────────────────────────────────
  set = (field) => (e) => this.setState({ [field]: e.target.value });

  handleLockTypeChange = (e) => {
    const lockType = e.target.value;
    const update = { lockType };
    if (lockType === 'STANDARD') {
      update.licenseType = 'NONE';
      update.operatingMode = 'PUBLIC';
      update.onlineSale = false;
      if (this.state.saleMode === 'revenue_share') update.saleMode = 'purchase';
    } else if (this.state.operatingMode === 'PUBLIC') {
      update.operatingMode = '24H';
      if (this.state.licenseType === 'NONE') update.licenseType = 'BASIC';
      update.pinRfidType = 'PIN';
    }
    this.setState(update);
  };

  handleSaleModeChange = (e) => {
    const saleMode = e.target.value;
    const update = { saleMode };
    if (saleMode === 'revenue_share') {
      // Revenue share requires Bluetooth + license
      if (this.state.lockType === 'STANDARD') update.lockType = 'BLUETOOTH';
      if (this.state.licenseType === 'NONE') update.licenseType = 'BASIC';
      if (this.state.operatingMode === 'PUBLIC') update.operatingMode = '24H';
    }
    this.setState(update);
  };

  setNum = (field) => (e) => {
    const v = parseFloat(e.target.value);
    this.setState({ [field]: isNaN(v) ? 0 : Math.max(0, Math.min(100, v)) });
  };

  setDiscount = (field, max = 100) => (e) => {
    const v = parseFloat(e.target.value);
    this.setState({ [field]: isNaN(v) ? 0 : Math.max(0, Math.min(max, v)) });
  };

  resetForm = () => {
    this.setState({
      clientName: '', clientCompany: '', clientCIF: '', clientAddress: '',
      proposalRef: '', notes: '',
      proposalDate: new Date().toISOString().slice(0, 10),
      licenseType: 'BASIC', maintenanceType: 'ESTANDAR', zone: 'ZONA_I',
      lockerRange: '0-500', customization: true, installationType: 'interior',
      lockType: 'BLUETOOTH', pinRfidType: 'PIN', lockCloseType: 'HOOK', saleMode: 'purchase',
      operatingMode: '24H',
      contractDuration: 60, depositMonths: 4, commissionPcts: [], hideMinAnnualCommission: false,
      salesYear1: '', salesYear2: '', salesYear3: '', existingLockers: '',
      installationCost: 0, conditions: '',
      discountProduct: 0, discountMaintenance: 0, discountLicense: 0, discountRental: 0,
      cabinetItems: [], posItems: [], spareItems: [], consumableItems: [],
      selectedCabinetCode: '', selectedPosCode: '', selectedConsumableCode: '', selectedSpareCode: '',
      configLine: [], catalogTab: 'interior', moduleMode: 'visual',
    });
  };

  setModuleMode = (mode) => {
    if (mode === this.state.moduleMode) return;
    if (mode === 'visual') {
      // Switching to visual: reset everything so the line starts fresh
      this.setState({ moduleMode: mode, configLine: [], cabinetItems: [], posItems: [], catalogTab: this.state.installationType });
    } else {
      // Switching to list: keep existing items, just clear the visual line
      this.setState({ moduleMode: mode, configLine: [] });
    }
  };

  // ─── Calculations ──────────────────────────────────────
  calculateTotals = () => {
    const { cabinetItems, posItems, spareItems, consumableItems, customization, saleMode,
            discountProduct, discountMaintenance, discountLicense, discountRental,
            contractDuration, depositMonths } = this.state;

    const sumItems = (items) => items.reduce((acc, it) => ({
      units: acc.units + it.qty,
      lockers: acc.lockers + it.qty * (it.lockers || 0),
      sale: acc.sale + it.qty * it.unitPrice,
      rent: acc.rent + it.qty * (it.unitRent || 0),
    }), { units: 0, lockers: 0, sale: 0, rent: 0 });

    const cab   = sumItems(cabinetItems);
    const pos   = sumItems(posItems);
    const spare = sumItems(spareItems || []);
    const consumableLockers = (consumableItems || []).reduce((s, it) => s + it.qty * (it.lockers || 0), 0);

    const totalLockers = cab.lockers + pos.lockers + spare.lockers + consumableLockers;
    const baseSale     = cab.sale + pos.sale;
    const baseRent     = cab.rent + pos.rent;

    const licenseMonthlyPerPOS = this.getLicensePricePerPOS();
    const totalPOS         = pos.units;
    const baseLicenseMonth = Math.round(totalPOS * licenseMonthlyPerPOS);
    const baseMaintYear    = this.getMaintenanceCost(totalLockers);

    const dp = 1 - discountProduct / 100;
    const dm = 1 - discountMaintenance / 100;
    const dl = 1 - discountLicense / 100;

    const sale              = Math.round(baseSale * dp);
    const spareSale         = Math.round(spare.sale * dp);
    const licenseMonthly    = Math.round(baseLicenseMonth * dl);
    const licenseAnnual     = licenseMonthly * 12;
    const maintenanceAnnual = Math.round(baseMaintYear * dm);
    // ── Rental financial formula (spec: 25% annual depreciation, 7% annual return) ──
    // Spare parts are included in the CAPEX base for rental/revenue_share (same as modules/POS).
    const ANNUAL_DEPRECIATION = 0.25;
    const USEFUL_LIFE_YEARS   = 1 / ANNUAL_DEPRECIATION; // 4
    const ANNUAL_RETURN_RATE  = 0.075;
    const rentalYears         = Math.min(contractDuration / 12, 5);
    const contractMonths      = Math.min(contractDuration, 60); // capped at 5 years

    const calcRentMonthly = (saleAmt, licMonthly, maintAnnual) => {
      const residual    = saleAmt * Math.max(1 - rentalYears / USEFUL_LIFE_YEARS, 0);
      const monthlyRate = Math.pow(1 + ANNUAL_RETURN_RATE, 1 / 12) - 1;
      let assetMonthly;
      if (monthlyRate === 0) {
        assetMonthly = (saleAmt - residual) / contractMonths;
      } else {
        assetMonthly =
          ((saleAmt - residual / Math.pow(1 + monthlyRate, contractMonths)) * monthlyRate) /
          (1 - Math.pow(1 + monthlyRate, -contractMonths));
      }
      return assetMonthly + licMonthly + maintAnnual / 12;
    };

    // rentalCapex includes spare parts (hardware capital fully recovered through rental)
    const rentalCapex     = sale + spareSale;
    const rentalBaseCapex = baseSale + spare.sale;
    const residualValue   = rentalCapex * Math.max(1 - rentalYears / USEFUL_LIFE_YEARS, 0);
    const rentMonthly     = Math.round(calcRentMonthly(rentalCapex, licenseMonthly, maintenanceAnnual));
    const rentAnnual      = rentMonthly * 12;
    const baseRentMonthly = calcRentMonthly(rentalBaseCapex, baseLicenseMonth, baseMaintYear);
    const baseRentAnnual  = Math.round(baseRentMonthly * 12);
    const rentalDiscountPct = baseRentAnnual > 0 ? Math.round((1 - rentAnnual / baseRentAnnual) * 100) : 0;
    const personalization   = customization ? [...cabinetItems, ...posItems].reduce((s, it) => {
      return s + it.qty * (it.personalizationPrice || 0);
    }, 0) : 0;
    const deposit           = rentMonthly * depositMonths;

    const consumableSale = (consumableItems || []).reduce((s, it) => s + it.qty * this.getConsumableUnitPrice(it), 0);

    const nonQrPosUnits = posItems.filter(it => !/qr/i.test(it.code)).reduce((s, it) => s + it.qty, 0);

    return {
      totalCabinets: cab.units, totalKiosks: pos.units, totalLockers,
      totalUnits: cab.units + pos.units + spare.units,
      totalModules: cab.units + nonQrPosUnits,
      sale, licenseMonthly, licenseAnnual, maintenanceAnnual,
      rentAnnual, rentMonthly, residualValue, baseRentAnnual, rentalDiscountPct, personalization, deposit,
      cabinetSale: cab.sale, cabinetRent: cab.rent, cabinetUnits: cab.units, cabinetLockers: cab.lockers,
      posSale: pos.sale, posRent: pos.rent, posUnits: pos.units,
      consumableSale, spareSale,
      // raw pre-discount bases
      baseSale, baseLicenseMonth, baseMaintYear, baseRent: cab.rent + pos.rent,
    };
  };

  // ─── PDF Export (Revenue-Share Study) ──────────────────
  saveRsStudyPdf = async () => {
    const el = this.printRsStudyRef.current;
    if (!el) return;
    this.setState({ generatingRsStudy: true });
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const PW = 210, PH = 297;
      const imgH = (canvas.height * PW) / canvas.width;
      pdf.addImage(imgData, 'JPEG', 0, 0, PW, imgH, undefined, 'NONE');
      let left = imgH - PH;
      let pos = -PH;
      while (left > 0) {
        pdf.addPage('a4', 'p');
        pdf.addImage(imgData, 'JPEG', 0, pos, PW, imgH, undefined, 'NONE');
        pos -= PH;
        left -= PH;
      }
      const ref  = this.state.proposalRef || 'propuesta';
      const date = this.state.proposalDate.replace(/-/g, '');
      const T = RS_T[this.state.exportLang || 'es'] || RS_T['es'];
      pdf.save(T.filename(ref, date));
    } catch (err) {
      console.error('RS Study PDF error:', err);
    } finally {
      this.setState({ generatingRsStudy: false });
    }
  };

  // ─── Render: Revenue-Share Study Page ──────────────────
  renderRsStudyPage() {
    const lang = this.state.exportLang || 'es';
    const T = RS_T[lang] || RS_T['es'];
    const MONTHS = T.months;
    const S = {
      page:   { padding: '20px 28px 16px', fontFamily: "'Segoe UI', system-ui, sans-serif", color: '#111', background: '#fff' },
      secHdr: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#fff', background: '#0a4080', padding: '3px 8px', marginBottom: 0 },
      table:  { width: '100%', borderCollapse: 'collapse', marginBottom: 8 },
      th:     { border: '1px solid #b0bec5', background: '#e3f2fd', padding: '5px 6px', fontSize: 9, fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', whiteSpace: 'nowrap' },
      thL:    { border: '1px solid #b0bec5', background: '#e3f2fd', padding: '5px 6px', fontSize: 9, fontWeight: 700, textAlign: 'left', textTransform: 'uppercase' },
      td:     { border: '1px solid #b0bec5', padding: '5px 6px', fontSize: 9, textAlign: 'center', minWidth: 52 },
      tdL:    { border: '1px solid #b0bec5', padding: '5px 6px', fontSize: 9, textAlign: 'left', fontWeight: 600, background: '#f8fafc' },
      tdNote: { border: '1px solid #b0bec5', padding: '4px 6px', fontSize: 8, textAlign: 'left', color: '#64748b', fontStyle: 'italic' },
    };
    const emptyTd = (i) => <td key={i} style={S.td}>&nbsp;</td>;
    return (
      <div style={S.page}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, borderBottom: '2px solid #0a4080', paddingBottom: 8 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: '#0a4080', marginBottom: 2 }}>{T.title}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>{T.subtitle}</div>
          </div>
          <img src="/images/logo_blue.png" alt="VACWAYlockers" style={{ height: 26, width: 'auto' }} />
        </div>

        {/* Section 1: Locker info */}
        <div style={S.secHdr}>{T.lockerConfig}</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.thL, width: '40%' }}>&nbsp;</th>
              <th style={S.th}>{T.type1}</th>
              <th style={S.th}>{T.type2}</th>
              <th style={S.th}>{T.total}</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={S.tdL}>{T.qtyLockers}</td>{[0,1,2].map(emptyTd)}</tr>
            <tr><td style={S.tdL}>{T.lockersSize}</td>{[0,1,2].map(emptyTd)}</tr>
            <tr><td style={S.tdL}>{T.lockersPrice}</td>{[0,1,2].map(emptyTd)}</tr>
            <tr><td colSpan={4} style={S.tdNote}>{T.note}</td></tr>
          </tbody>
        </table>

        {/* Sections 2 & 3 stacked full width */}
        {/* Lockers Revenue */}
        <div style={S.secHdr}>{T.lockersRevenue}</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.thL, width: '28%' }}>{T.month}</th>
              <th style={S.th}>2025 [€]</th>
              <th style={S.th}>2024 [€]</th>
              <th style={S.th}>2023 [€]</th>
            </tr>
          </thead>
          <tbody>
            {MONTHS.map(m => (
              <tr key={m}><td style={S.tdL}>{m}</td>{[0,1,2].map(emptyTd)}</tr>
            ))}
          </tbody>
        </table>

        {/* Park Attendance */}
        <div style={S.secHdr}>{T.parkAttendance}</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.thL, width: '28%' }}>{T.month}</th>
              <th style={S.th}>2025 [PAX]</th>
              <th style={S.th}>2024 [PAX]</th>
              <th style={S.th}>2023 [PAX]</th>
            </tr>
          </thead>
          <tbody>
            {MONTHS.map(m => (
              <tr key={m}><td style={S.tdL}>{m}</td>{[0,1,2].map(emptyTd)}</tr>
            ))}
          </tbody>
        </table>

        {/* Signature block */}
        <div style={{ marginTop: 8, borderTop: '2px solid #0a4080', paddingTop: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#0a4080', marginBottom: 6 }}>{T.signatureStamp}</div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'stretch' }}>
            {/* 2-column grid of fields */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px' }}>
              {T.sigFields.map(lbl => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#0a4080', whiteSpace: 'nowrap', width: 62 }}>{lbl}:</span>
                  <div style={{ flex: 1, borderBottom: '1px solid #555', height: 14 }} />
                </div>
              ))}
            </div>
            {/* Stamp */}
            <div style={{ width: 120, border: '1px solid #b0bec5', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 9, color: '#b0bec5', fontStyle: 'italic' }}>{T.stampArea}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── PDF Export ────────────────────────────────────────
  savePdf = async () => {
    const coverEl  = this.printCoverRef.current;
    const visualEl = this.printVisualRef.current;
    const dataEl   = this.printDataRef.current;
    const ofertaEl = this.printOfertaRef.current;
    if (!coverEl || !visualEl || !dataEl || !ofertaEl) return;
    this.setState({ generatingPdf: true });
    try {
      const PORTRAIT  = { w: 210, h: 297 };
      const LANDSCAPE = { w: 297, h: 210 };
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const renderNode = async (el, orientation, isFirstPage) => {
        const page = orientation === 'l' ? LANDSCAPE : PORTRAIT;
        const canvas = await html2canvas(el, {
          scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const imgW = page.w;
        const imgH = (canvas.height * imgW) / canvas.width;
        if (!isFirstPage) pdf.addPage('a4', orientation);
        if (imgH <= page.h) {
          pdf.addImage(imgData, 'JPEG', 0, 0, imgW, imgH, undefined, 'NONE');
        } else {
          let pos = 0;
          pdf.addImage(imgData, 'JPEG', 0, pos, imgW, imgH, undefined, 'NONE');
          let left = imgH - page.h;
          while (left > 0) {
            pos -= page.h;
            pdf.addPage('a4', orientation);
            pdf.addImage(imgData, 'JPEG', 0, pos, imgW, imgH, undefined, 'NONE');
            left -= page.h;
          }
        }
      };
      await renderNode(coverEl, 'p', true);
      await renderNode(dataEl, 'p', false);
      await renderNode(ofertaEl, 'p', false);
      if (this.state.moduleMode === 'visual') {
        await renderNode(visualEl, 'l', false);
      }
      const lang = this.state.exportLang || 'es';
      const T = PDF_T[lang] || PDF_T['es'];
      const ref  = this.state.proposalRef || 'proposal';
      const date = this.state.proposalDate.replace(/-/g, '');
      pdf.save(T.filename(ref, date));
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      this.setState({ generatingPdf: false });
    }
  };

  // ─── Render: Visual item list (below drop zone) ───────
  renderVisualItemList() {
    const { cabinetItems, posItems, saleMode, installationType } = this.state;
    const allItems = [...cabinetItems, ...posItems];
    if (allItems.length === 0) return null;
    const isExt  = installationType === 'exterior';
    const showSale = saleMode === 'purchase';
    const priceLabel = showSale ? (isExt ? 'Precio Ext.' : 'Precio Int.') : (isExt ? 'Alq./mes Ext.' : 'Alq./mes Int.');
    const thS = { padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: TH_COLOR, background: TH_BG, borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap' };
    const tdS = { padding: '7px 10px', textAlign: 'right', borderBottom: `1px solid #f1f5f9`, fontSize: 13, color: TEXT_1 };
    const total = allItems.reduce((a, it) => ({
      units: a.units + it.qty, lockers: a.lockers + it.qty * it.lockers,
      sale: a.sale + it.qty * it.unitPrice, rent: a.rent + it.qty * it.unitRent,
    }), { units: 0, lockers: 0, sale: 0, rent: 0 });
    return (
      <div style={{ borderTop: `1px solid ${BORDER}` }}>
        <div style={{ padding: '12px 24px 8px' }}>
          <div style={sectionTitle}>Productos configurados</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ ...thS, textAlign: 'left', minWidth: 200 }}>Producto</th>
                <th style={{ ...thS, minWidth: 70 }}>Tipo</th>
                {showSale && <th style={{ ...thS, minWidth: 80 }}>{priceLabel}</th>}
                <th style={{ ...thS, minWidth: 60 }}>Cant.</th>
                <th style={{ ...thS, minWidth: 60 }}>Taquillas</th>
                {showSale && <th style={{ ...thS, minWidth: 85 }}>Total Venta</th>}
              </tr>
            </thead>
            <tbody>
              {allItems.map((it, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? ROW_HL : '#fff' }}>
                  <td style={{ ...tdS, textAlign: 'left', fontWeight: 600 }}>
                    <div style={{ fontWeight: 700 }}>{it.envCode || it.code}</div>
                    {it.envCode && it.envCode !== it.code && <div style={{ fontSize: 10, color: TEXT_2, fontWeight: 400 }}>{it.code}</div>}
                  </td>
                  <td style={{ ...tdS, textAlign: 'left' }}>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 700, background: it.category === 'pos' ? '#fef3c7' : '#e0f2fe', color: it.category === 'pos' ? '#92400e' : '#0369a1' }}>
                      {it.category === 'pos' ? 'Kiosco' : 'Armario'}
                    </span>
                  </td>
                  {showSale && <td style={tdS}>{fmtAlways(it.unitPrice)}</td>}
                  <td style={{ ...tdS, fontWeight: 600 }}>{it.qty}</td>
                  <td style={{ ...tdS, fontWeight: 600 }}>{it.qty * it.lockers}</td>
                  {showSale && <td style={{ ...tdS, fontWeight: 600, color: TEAL }}>{fmtAlways(it.qty * it.unitPrice)}</td>}
                </tr>
              ))}
              <tr style={{ background: '#f8fafc' }}>
                <td style={{ ...tdS, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>Total</td>
                <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                {showSale && <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>}
                <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{total.units}</td>
                <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{total.lockers}</td>
                {showSale && <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none', color: TEAL }}>{fmtAlways(total.sale)}</td>}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ─── Render: Visual Configurator Section ──────────────
  renderVisualSection() {
    const { products } = this.props;
    const { installationType, catalogTab, configLine, moduleMode, lockType } = this.state;
    const envLocked = configLine.some(m => m.category === 'cabinet');
    const cabinets   = this.getProducts('cabinet');
    const posProducts = this.getProducts('pos');
    const posDisabled = lockType === 'STANDARD';

    // ── Shared header bar ──────────────────────────────
    const modeSwitch = (
      <div style={{ padding: '14px 24px 12px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={sectionTitle}>Vista de Instalación</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {/* Mode switch */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: TEXT_2 }}>Modo:</span>
            {[{ v: 'visual', label: 'Visual' }, { v: 'list', label: 'Lista' }].map(({ v, label }) => (
              <button key={v} type="button" onClick={() => this.setModuleMode(v)}
                style={{
                  padding: '4px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6,
                  cursor: 'pointer', transition: 'all 0.15s',
                  background: moduleMode === v ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#f1f5f9',
                  color: moduleMode === v ? '#fff' : TEXT_2,
                }}>
                {label}
              </button>
            ))}
          </div>
          {/* Environment tabs – visual mode: Interior / Exterior / PdV */}
          {moduleMode === 'visual' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: TEXT_2 }}>Entorno:</span>
              {[
                { v: 'interior', label: 'Interior' },
                { v: 'exterior', label: 'Exterior' },
                { v: 'pos',      label: 'Puntos de venta' },
              ].map(({ v, label }) => {
                const isLocked = envLocked && v !== 'pos' && installationType !== v;
                const isPosBlocked = v === 'pos' && posDisabled;
                const disabled = isLocked || isPosBlocked;
                return (
                  <button key={v} type="button"
                    disabled={disabled}
                    onClick={() => !disabled && this.handleInstallationChange(v)}
                    title={isLocked ? 'Limpia la linea para cambiar de entorno' : isPosBlocked ? 'No disponible para cerradura Standard' : ''}
                    style={{
                      padding: '5px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6,
                      cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                      background: catalogTab === v ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#f1f5f9',
                      color: catalogTab === v ? '#fff' : TEXT_2,
                      opacity: disabled ? 0.4 : 1,
                    }}>
                    {label}{isLocked ? ' 🔒' : ''}
                  </button>
                );
              })}
            </div>
          )}
          {/* Environment tabs – list mode: Interior / Exterior only */}
          {moduleMode === 'list' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: TEXT_2 }}>Entorno:</span>
              {[{ v: 'interior', label: 'Interior' }, { v: 'exterior', label: 'Exterior' }].map(({ v, label }) => (
                <button key={v} type="button" onClick={() => this.handleInstallationChange(v)}
                  style={{
                    padding: '5px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6,
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: installationType === v ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#f1f5f9',
                    color: installationType === v ? '#fff' : TEXT_2,
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    // ── List mode ──────────────────────────────────────
    if (moduleMode === 'list') {
      return (
        <div>
          <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
            {modeSwitch}
          </div>
          {this.renderProductSection('Armarios de taquillas', 'cabinet', this.state.cabinetItems, cabinets)}
          {!posDisabled && this.renderProductSection('Puntos de venta', 'pos', this.state.posItems, posProducts)}
        </div>
      );
    }

    // ── Visual mode ────────────────────────────────────
    return (
      <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        {modeSwitch}
        <div style={{ padding: 20 }}>
          <VisualLockerLine
            products={products || []}
            env={catalogTab}
            lockedEnv={envLocked ? installationType : null}
            configLine={configLine}
            onChange={this.onVisualLineChange}
            spareItems={this.state.spareItems || []}
          />
        </div>
        {configLine.length > 0 && (
          <div style={{ padding: '8px 24px 14px', borderTop: `1px solid ${BORDER}`, fontSize: 12, color: TEXT_2 }}>
            <strong>{configLine.length}</strong> modulos colocados &nbsp;|&nbsp;
            <strong>{[...this.state.cabinetItems, ...this.state.posItems].reduce((s, it) => s + it.qty * it.lockers, 0)}</strong> taquillas totales
          </div>
        )}
        {this.renderVisualItemList()}
      </div>
    );
  }

  // ─── Render: Print Visual Line ─────────────────────────
  renderPrintVisualLine() {
    const { configLine, installationType } = this.state;
    if (!configLine || configLine.length === 0) return null;
    const env = installationType === 'exterior' ? 'exterior' : 'interior';
    const totalCm = configLine.reduce((s, m) => s + (m._imgWidth ?? 65.7), 0);
    const totalLockers = configLine.reduce((s, m) => s + (m.lockers || 0), 0);
    const totalDisplay = totalCm >= 100
      ? `${(totalCm / 100).toFixed(3).replace(/\.?0+$/, '')} m`
      : `${parseFloat(totalCm.toFixed(2))} cm`;
    const height = env === 'exterior' ? '1.94 m' : '2.035 m';

    // SVG viewBox aspect ratios (width/height) for explicit sizing in html2canvas
    const ASPECT = {
      'exterior_VLK06': 909/2590, 'exterior_VLM02': 548/1558, 'exterior_VLM04': 548/1558,
      'exterior_VLM04XL': 771/1151, 'exterior_VLM06': 548/1559, 'exterior_VLM06XL': 615/1349,
      'exterior_VLM10': 468/1330, 'exterior_VLM12': 493/1399,
      'interior_VLK08': 483/1322, 'interior_VLM04': 566/1551, 'interior_VLM04XL': 933/1370,
      'interior_VLM06': 567/1551, 'interior_VLM06XL': 544/1301,
      'interior_VLM10': 567/1552, 'interior_VLM12': 567/1551,
    };
    const IMG_H = 400;
    const getImgW = (m) => {
      const img = m.image || '';
      if (!img || img === 'qr') return 140; // QR square container
      const prefix = env === 'exterior' ? 'exterior' : 'interior';
      const key = `${prefix}_${img}`;
      const ratio = ASPECT[key] || 0.36;
      return Math.round(IMG_H * ratio);
    };

    const arrowL = { width: 0, height: 0, borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderRight: '6px solid #888', flexShrink: 0 };
    const arrowR = { width: 0, height: 0, borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft:  '6px solid #888', flexShrink: 0 };
    const arrowU = { width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '6px solid #888', flexShrink: 0 };
    const arrowD = { width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop:    '6px solid #888', flexShrink: 0 };
    const lineH  = { flex: 1, height: 1, background: '#888' };
    const lineV  = { flex: 1, width: 1, background: '#888' };

    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>
          Vista de Instalación
        </div>

        {/* Width dimension line */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0 2px 0', marginRight: 36 }}>
          <span style={arrowL} />
          <span style={lineH} />
          <span style={{ padding: '0 10px', fontSize: 10, fontWeight: 700, color: '#444', whiteSpace: 'nowrap', fontFamily: 'Courier New, monospace' }}>
            {totalDisplay} — {totalLockers} taquillas
          </span>
          <span style={lineH} />
          <span style={arrowR} />
        </div>

        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Modules area */}
          <div style={{
            flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 0,
            background: 'linear-gradient(180deg, #f8fbff 0%, #edf3fa 100%)',
            border: '2px solid #d0d8e2', borderRadius: 8,
            padding: '16px 40px 0 40px', position: 'relative', minHeight: 420,
          }}>
            {/* Floor line */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
              background: 'linear-gradient(90deg, #c4cdd8, #a0adb8, #c4cdd8)', borderRadius: '2px 2px 0 0' }} />

            {configLine.map((m, idx) => {
              const tag = (m.code || m.name || '').replace(/^(POS_)?(INT_|EXT_)?/, '').split(/[\s-]/)[0];
              const w = getImgW(m);
              return (
                <div key={m.uid || idx} style={{
                  display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                  flexShrink: 0, position: 'relative', zIndex: 1,
                  margin: 0, padding: 0, lineHeight: 0,
                }}>
                  {/* Use background-image: html2canvas respects background-size on sized divs */}
                  <div style={{
                    width: w, height: IMG_H,
                    backgroundImage: `url(${getModuleImage(m, env)})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center bottom',
                    flexShrink: 0,
                    ...(m.image === 'qr' || !m.image ? { backgroundSize: '70%', backgroundPosition: 'center center' } : {}),
                  }} />
                  <span style={{
                    fontSize: 8, fontWeight: 700, color: '#022040', textAlign: 'center',
                    whiteSpace: 'nowrap', lineHeight: 'normal',
                    margin: 0, padding: '2px 0 6px',
                  }}>
                    {tag}
                  </span>
                </div>
              );
            })}

            {/* Spare parts inline */}
            {(this.state.spareItems || []).length > 0 && configLine.length > 0 && (
              <div style={{ width: 2, alignSelf: 'stretch', background: '#b0bac4', margin: '24px 20px 4px', flexShrink: 0 }} />
            )}
            {(this.state.spareItems || []).map((it, idx) => {
              const prod = (this.props.products || []).find(p => p._id === it.productId);
              const imgKey = prod ? (prod.image || '') : '';
              const imgSrc = !imgKey ? null
                : imgKey === 'qr' ? '/images/configurator/qr.png'
                : (imgKey.startsWith('key_') || imgKey.startsWith('lock_') || imgKey === 'final') ? `/images/configurator/${imgKey}.png`
                : `/images/configurator/interior_${imgKey}.svg`;
              return (
                <div key={idx} style={{
                  display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                  flexShrink: 0, position: 'relative', zIndex: 1,
                  margin: '0 8px', padding: 0, lineHeight: 0,
                }}>
                  <span style={{
                    position: 'absolute', top: 8, right: -6,
                    background: '#8b5cf6', color: '#fff', fontSize: 11, fontWeight: 700,
                    borderRadius: 999, minWidth: 22, height: 22, padding: '0 6px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3,
                    boxSizing: 'border-box', whiteSpace: 'nowrap',
                  }}>{it.qty}</span>
                  {imgSrc
                    ? <div style={{ width: 80, height: IMG_H * 0.4, backgroundImage: `url(${imgSrc})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center bottom' }} />
                    : <div style={{ width: 80, height: IMG_H * 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, color: '#8b5cf6' }}>🔧</div>
                  }
                  <span style={{ fontSize: 8, fontWeight: 700, color: '#5b21b6', textAlign: 'center', whiteSpace: 'nowrap', lineHeight: 'normal', margin: 0, padding: '2px 0 6px' }}>
                    {it.code}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Height dimension */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 6px', minWidth: 32, flexShrink: 0 }}>
            <span style={arrowU} />
            <span style={lineV} />
            <span style={{
              writingMode: 'vertical-rl', textOrientation: 'mixed',
              fontSize: 9, fontWeight: 700, color: '#444', padding: '4px 0',
              fontFamily: 'Courier New, monospace', whiteSpace: 'nowrap',
            }}>
              {height}
            </span>
            <span style={lineV} />
            <span style={arrowD} />
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Hero ──────────────────────────────────────
  renderHero() {
    const { savedProposalId, proposalRef, clientName, savingProposal, saveSuccess, showPreview, generatingPdf, generatingRsStudy, exportLang, langDropdownOpen, cabinetItems, posItems } = this.state;
    const hasModules = cabinetItems.length > 0 || posItems.length > 0 || this.state.spareItems.length > 0;
    const isEditing = !!savedProposalId;

    const badge = isEditing
      ? <span className="pconf-mode-badge editing">Editando propuesta</span>
      : <span className="pconf-mode-badge new">Nueva propuesta</span>;

    const subtitle = isEditing
      ? `${proposalRef ? `Ref: ${proposalRef}` : 'Sin referencia'}${clientName ? ` · ${clientName}` : ''}`
      : 'Configura y genera propuestas comerciales para instalaciones VACWAYlockers';

    return (
      <div className={`pconf-header${isEditing ? ' editing' : ''}`}>
        <div className="pconf-header-content">
          <div className="pconf-header-text">
            <h1>
              <FileText size={28} className="pconf-header-icon" />
              Configurador de Propuestas
              {badge}
            </h1>
            <span className="pconf-subtitle">
              {subtitle}
            </span>
          </div>
          <div className="pconf-header-actions">
            <button onClick={() => this.setState(p => ({ showPreview: !p.showPreview }))}
              className="pconf-header-btn outline"
              disabled={!hasModules}
              title={!hasModules ? 'Añade al menos un módulo para usar la vista previa' : undefined}>
              {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
              {showPreview ? 'Editor' : 'Vista previa'}
            </button>
            <button onClick={this.resetForm}
              className="pconf-header-btn outline">
              <RotateCcw size={14} /> Limpiar
            </button>
            <button
              onClick={this.saveProposal}
              disabled={savingProposal || !hasModules}
              title={!hasModules ? 'Añade al menos un módulo para guardar' : undefined}
              className={`pconf-header-btn ${isEditing ? 'save-update' : 'outline'}`}
            >
              {saveSuccess
                ? <><CheckCircle size={14} /> Guardada</>
                : <><Save size={14} /> {savingProposal ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Guardar')}</>}
            </button>
            <button
              onClick={() => this.props.history && this.props.history.push('/proposals')}
              className="pconf-header-btn outline"
              title="Ver todas las propuestas"
            >
              <List size={14} /> Propuestas
            </button>
            {/* Language selector dropdown */}
            {(() => {
              const selectedLang = LANGUAGES.find(l => l.code === exportLang) || LANGUAGES[0];
              return (
                <div style={{ position: 'relative' }}>
                  <button type="button"
                    className="pconf-header-btn outline"
                    onClick={() => this.setState(p => ({ langDropdownOpen: !p.langDropdownOpen }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 10 }}>
                    <img src={`/languages/${selectedLang.imageId}.png`} alt={selectedLang.label} style={{ width: 20, height: 20, borderRadius: 2 }} />
                    <span style={{ fontSize: 13 }}>{selectedLang.label}</span>
                    <ChevronDown size={12} style={{ opacity: 0.6 }} />
                  </button>
                  {langDropdownOpen && (
                    <div
                      style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                      onClick={() => this.setState({ langDropdownOpen: false })}
                    />
                  )}
                  {langDropdownOpen && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100,
                      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '4px 0', minWidth: 155,
                    }}>
                      {LANGUAGES.map(({ code, imageId, label }) => (
                        <button key={code} type="button"
                          onClick={() => this.setState({ exportLang: code, langDropdownOpen: false })}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            width: '100%', padding: '8px 14px', border: 'none',
                            cursor: 'pointer', fontSize: 13, textAlign: 'left',
                            background: exportLang === code ? '#f0f9ff' : 'transparent',
                            color: exportLang === code ? '#0a4080' : '#1e293b',
                            fontWeight: exportLang === code ? 700 : 400,
                          }}>
                          <img src={`/languages/${imageId}.png`} alt={label} style={{ width: 22, height: 22, borderRadius: 2 }} />
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <button onClick={this.savePdf} disabled={generatingPdf || !hasModules}
              title={!hasModules ? 'Añade al menos un módulo para exportar el PDF' : `Exportar PDF (${(LANGUAGES.find(l => l.code === exportLang) || LANGUAGES[0]).label})`}
              className="pconf-header-btn primary">
              <Download size={14} /> {generatingPdf ? '...' : 'Oferta'}
            </button>
            <a href={anexoUrl(exportLang)} target="_blank" rel="noopener noreferrer"
              title={`Descargar Anexo Comercial (${(LANGUAGES.find(l => l.code === exportLang) || LANGUAGES[0]).label})`}
              className="pconf-header-btn outline">
              <FileText size={14} /> Anexo
            </a>
            <button onClick={this.saveRsStudyPdf} disabled={generatingRsStudy}
              title={`Descargar formulario Revenue-Share Study para el cliente (${(LANGUAGES.find(l => l.code === exportLang) || LANGUAGES[0]).label})`}
              className="pconf-header-btn outline">
              <Download size={14} /> {generatingRsStudy ? 'Generating...' : 'RS Study'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Summary Banner ────────────────────────────
  renderSummaryBanner(t) {
    const { licenseType, maintenanceType, customization, installationType,
            lockType, pinRfidType, lockCloseType, saleMode, operatingMode, onlineSale, contractDuration, depositMonths,
            commissionPcts, installationCost,
            discountProduct, discountMaintenance, discountLicense, discountRental,
            cabinetItems, posItems, spareItems, consumableItems } = this.state;
    const instLbl = INSTALLATION_TYPES.find(i => i.value === installationType)?.label || '';
    const lockLbl = LOCK_TYPES.find(l => l.value === lockType)?.label || '';
    const modeLbl = SALE_MODES.find(m => m.value === saleMode)?.label || '';
    const maintLbl = MAINTENANCE_TYPES.find(m => m.value === maintenanceType)?.label || '';
    const opModeLbl = OPERATING_MODES.find(m => m.value === operatingMode)?.label || operatingMode || '';

    const kv = (label, value, bold) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontSize: 13, color: TEXT_2 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: TEXT_1 }}>{value}</span>
      </div>
    );

    // Shows base, discount row, and discounted price when a discount > 0 is applied
    const kvDiscount = (label, baseValue, discountedValue, discountPct, bold, suffix = '') => {
      if (!discountPct || discountPct <= 0) return kv(label, fmtAlways(discountedValue) + suffix, bold);
      const saving = discountedValue - baseValue; // negative number
      return (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 13, color: TEXT_2 }}>{label}</span>
            <span style={{ fontSize: 13, color: TEXT_1 }}>{fmtAlways(baseValue)}{suffix}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: 'none' }}>
            <span style={{ fontSize: 12, color: AMBER, fontWeight: 600 }}>Dto. {discountPct}%</span>
            <span style={{ fontSize: 12, color: AMBER, fontWeight: 600 }}>{fmtAlways(saving)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0 5px', borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 12, color: TEXT_2 }}>Total</span>
            <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: GREEN }}>{fmtAlways(discountedValue)}{suffix}</span>
          </div>
        </>
      );
    };

    const sectionHdr = (label, color) => (
      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: color || TEAL, margin: '16px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color || TEAL, display: 'inline-block' }} />
        {label}
      </div>
    );

    const itemLine = (item, idx) => (
      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
        <span style={{ color: TEXT_2 }}>{item.name} <span style={{ color: TEXT_3 }}>×{item.qty}</span></span>
        <span style={{ fontWeight: 600, color: TEXT_1 }}>{item.qty * item.lockers} taq.</span>
      </div>
    );

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Left – Config overview */}
        <div style={{ ...cardStyle, background: '#fafbfc' }}>
          <div style={sectionTitle}>Configuración Oferta</div>
          {kv('Módulos', t.totalModules)}
          {kv('Taquillas', t.totalLockers)}
          {kv('Tipo Instalación', instLbl)}
          {kv('Tipo Cerradura', lockLbl)}
          {kv('Acceso', pinRfidType)}
          {kv('Modo Funcionamiento', opModeLbl)}
          {kv('Venta Online', onlineSale ? 'Sí' : 'No')}
          {kv('Tipo de Contrato', modeLbl, true)}
          {kv('Licencia', licenseType)}
          {kv('Mantenimiento', MAINTENANCE_TYPES.find(m => m.value === maintenanceType)?.label)}
          {kv('Personalización', customization ? 'Sí' : 'No')}
        </div>

        {/* Center – Selected modules & kiosks */}
        <div style={{ ...cardStyle, background: '#fafbfc' }}>
          <div style={sectionTitle}>Productos Seleccionados</div>
          {cabinetItems.length > 0 && (
            <>
              {sectionHdr('Armarios')}
              {cabinetItems.map((it, i) => itemLine(it, i))}
            </>
          )}
          {posItems.length > 0 && (
            <>
              {sectionHdr('Puntos de venta')}
              {posItems.map((it, i) => itemLine(it, i))}
            </>
          )}
          {consumableItems && consumableItems.length > 0 && (
            <>
              {sectionHdr('Consumibles', AMBER)}
              {consumableItems.map((it, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
                  <span style={{ color: TEXT_2 }}>{it.name} <span style={{ color: TEXT_3 }}>×{it.qty}</span></span>
                </div>
              ))}
            </>
          )}
          {spareItems && spareItems.length > 0 && (
            <>
              {sectionHdr('Recambios', '#8b5cf6')}
              {spareItems.map((it, i) => itemLine(it, i))}
            </>
          )}
          {cabinetItems.length === 0 && posItems.length === 0 && (!consumableItems || consumableItems.length === 0) && (!spareItems || spareItems.length === 0) && (
            <div style={{ padding: '20px 0', textAlign: 'center', color: TEXT_3, fontSize: 13, fontStyle: 'italic' }}>
              Sin productos añadidos
            </div>
          )}
        </div>

        {/* Right – CAPEX / OPEX */}
        <div style={cardStyle}>
          {saleMode === 'purchase' && (
            <>
              {sectionHdr('CAPEX')}
              {kvDiscount('Taquillas Smart VACWAYlockers (' + instLbl + ')', t.baseSale, t.sale, discountProduct, true)}
              {t.consumableSale > 0 && kv('Consumibles', fmtAlways(t.consumableSale))}
              {t.spareSale > 0 && kv('Recambios', fmtAlways(t.spareSale))}
              {kv('Personalización', customization ? fmtAlways(t.personalization) : 'No incluida')}
              {kv('Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : 'Pendiente')}
              {(() => { const totalCapex = t.sale + t.consumableSale + (t.spareSale || 0) + (customization ? t.personalization : 0) + (installationCost > 0 ? installationCost : 0); return kv('TOTAL CAPEX', fmtAlways(totalCapex), true); })()}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && sectionHdr('OPEX', GREEN)}
              {licenseType !== 'NONE' && kvDiscount('Licencias (' + licenseType + ')', t.baseLicenseMonth * 12, t.licenseAnnual, discountLicense, false, ' /año')}
              {maintenanceType !== 'NONE' && kvDiscount('Mantenimiento (' + maintLbl + ')', t.baseMaintYear, t.maintenanceAnnual, discountMaintenance, false, ' /año')}
              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && (() => { const totalOpex = (licenseType !== 'NONE' ? t.licenseAnnual : 0) + (maintenanceType !== 'NONE' ? t.maintenanceAnnual : 0); return kv('TOTAL OPEX', fmtAlways(totalOpex) + ' /año', true); })()}
            </>
          )}

          {saleMode === 'rental' && (
            <>
              {sectionHdr('ALQUILER')}
              {t.rentalDiscountPct > 0 && (() => {
                return (<>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', fontSize: 12, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                    <span>Precio base</span><span>{fmtAlways(t.baseRentAnnual) + ' /año'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', fontSize: 12, color: '#059669', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>
                    <span>Descuento global</span><span>-{t.rentalDiscountPct}%</span>
                  </div>
                </>);
              })()}
              {kv('Alquiler anual (' + instLbl + ')', fmtAlways(t.rentAnnual) + ' /año', true)}
              {kv('Alquiler mensual (' + instLbl + ')', fmtAlways(t.rentMonthly) + ' /mes', true)}
              {kv('Depósito (' + depositMonths + ' meses)', fmtAlways(t.deposit))}
              {kv('Duración contrato', Math.round(contractDuration / 12) + ' años')}
              {kv('Personalización', customization ? fmtAlways(t.personalization) : 'No incluida')}
              {kv('Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : 'Pendiente')}
              {t.consumableSale > 0 && kv('Consumibles (CAPEX)', fmtAlways(t.consumableSale))}
            </>
          )}

          {saleMode === 'revenue_share' && (
            <>
              {sectionHdr('REVENUE SHARE')}
              {kv('Duración contrato', Math.round(contractDuration / 12) + ' años')}
              {commissionPcts.map((pct, i) => kv(`Comisión Cliente Año ${i + 1}`, fmtPct(pct)))}
              {!this.state.hideMinAnnualCommission && kv('Comisión mínima anual (equivalente)', fmtAlways(t.rentAnnual) + ' /año', true)}
              {kv('Personalización', customization ? fmtAlways(t.personalization) : '-')}
              {kv('Instalación, formación y puesta en marcha', installationCost > 0 ? fmtAlways(installationCost) : 'Pendiente')}
              {t.consumableSale > 0 && kv('Consumibles (CAPEX)', fmtAlways(t.consumableSale))}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && sectionHdr('OPEX', GREEN)}
              {licenseType !== 'NONE' && kv('Licencias', 'Incluidas')}
              {maintenanceType !== 'NONE' && kv('Mantenimiento', 'Incluido')}
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Render: Input Section ─────────────────────────────
  renderInputSection() {
    const { clientName, clientCompany, clientCIF, clientAddress,
            proposalRef, proposalDate, notes,
            licenseType, maintenanceType, zone, lockerRange, customization, installationType,
            lockType, pinRfidType, lockCloseType, saleMode, operatingMode, onlineSale, contractDuration, depositMonths,
            commissionPcts, installationCost, conditions,
            discountProduct, discountMaintenance, discountLicense, discountRental,
            salesYear1, salesYear2, salesYear3, existingLockers } = this.state;

    const field = (lbl, value, onChange, type = 'text', extra = {}) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <input type={type} value={value} onChange={onChange} style={{ ...inputStyle, ...extra }} />
      </div>
    );

    const sel = (lbl, value, onChange, options) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <select value={value} onChange={onChange} style={selectStyle}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );

    const toggle2 = (lbl, value, onChange, options, disabledValues = []) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
          {options.map((o, idx) => {
            const active = o.value === value;
            const disabled = disabledValues.includes(o.value);
            return (
              <button key={o.value} type="button"
                disabled={disabled}
                onClick={() => !disabled && onChange({ target: { value: o.value } })}
                style={{
                  flex: 1, padding: '9px 12px', fontSize: 13, fontWeight: 600,
                  borderLeft: idx > 0 ? `1px solid ${BORDER}` : 'none',
                  borderTop: 'none', borderRight: 'none', borderBottom: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                  background: active ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : disabled ? '#f1f5f9' : '#fff',
                  color: active ? '#fff' : disabled ? TEXT_3 : TEXT_2,
                  opacity: disabled ? 0.5 : 1,
                }}>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );

    const toggleSwitch = (lbl, checked, onChange) => (
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>{lbl}</label>
        <button type="button" onClick={() => onChange(!checked)}
          style={{
            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: checked ? '#0a4080' : '#cbd5e1', position: 'relative', transition: 'background 0.2s',
            flexShrink: 0,
          }}>
          <span style={{
            position: 'absolute', top: 2, left: checked ? 22 : 2,
            width: 20, height: 20, borderRadius: '50%', background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
          }} />
        </button>
      </div>
    );

    const pctField = (lbl, value, onChange) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ position: 'relative' }}>
          <input type="number" min="0" max="100" step="0.5" value={value} onChange={onChange}
                 style={{ ...inputStyle, paddingRight: 32 }} />
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: TEXT_3 }}>%</span>
        </div>
      </div>
    );

    const numField = (lbl, value, onChange, suffix = '') => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        <div style={{ position: 'relative' }}>
          <input type="number" min="0" value={value} onChange={onChange}
                 style={{ ...inputStyle, paddingRight: suffix ? 40 : 12 }} />
          {suffix && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: TEXT_3 }}>{suffix}</span>}
        </div>
      </div>
    );

    return (
      <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 20 }}>
        {/* Configuration */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Configuración</div>
          {toggle2('Tipo de Cerradura', lockType, this.handleLockTypeChange, LOCK_TYPES, saleMode === 'revenue_share' ? ['STANDARD'] : [])}
          {toggle2('Acceso', pinRfidType, this.set('pinRfidType'), LOCK_ACCESS_TYPES)}
          {toggle2('Tipo de Licencia', licenseType, this.set('licenseType'), LICENSE_TYPES, lockType === 'STANDARD' ? ['BASIC', 'PRO'] : saleMode === 'revenue_share' ? ['NONE'] : (lockType === 'BLUETOOTH' && onlineSale) ? ['NONE', 'BASIC'] : lockType === 'BLUETOOTH' ? ['NONE'] : [])}
          {toggle2('Tipo de Mantenimiento', maintenanceType, this.set('maintenanceType'), MAINTENANCE_TYPES)}
          {toggle2(
            'Modo de Funcionamiento',
            operatingMode,
            this.set('operatingMode'),
            lockType === 'STANDARD' ? OPERATING_MODES : OPERATING_MODES.filter(m => m.value !== 'PUBLIC'),
            lockType === 'STANDARD'
              ? OPERATING_MODES.filter(m => m.value !== 'PUBLIC').map(m => m.value)
              : ['PUBLIC']
          )}
          {toggle2('Venta Online', onlineSale, (e) => {
            const val = e.target.value === true;
            const update = { onlineSale: val };
            if (val && this.state.licenseType !== 'PRO') update.licenseType = 'PRO';
            this.setState(update);
          }, [{ value: false, label: 'No' }, { value: true, label: 'Sí' }], lockType === 'STANDARD' ? [true] : [])}
          {toggle2('Personalización', customization, (e) => this.setState({ customization: e.target.value === true }), [{ value: false, label: 'No' }, { value: true, label: 'Sí' }])}
          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Observaciones</label>
            <textarea value={notes} onChange={this.set('notes')} rows={2}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>

        {/* Client Info */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Datos del Cliente</div>
          {field('Empresa / Razón social', clientCompany, this.set('clientCompany'))}
          {field('CIF / NIF', clientCIF, this.set('clientCIF'))}
          {field('Dirección', clientAddress, this.set('clientAddress'))}
          {field('Persona de contacto', clientName, this.set('clientName'))}
          {field('Referencia propuesta', proposalRef, this.set('proposalRef'))}
          {field('Fecha', proposalDate, this.set('proposalDate'), 'date')}
          {sel('Zona de Instalación', zone, this.set('zone'), ZONES)}
        </div>

        {/* Sale Mode + Conditions */}
        <div style={cardStyle}>
          <div style={sectionTitle}>Tipo de Contrato</div>
          {toggle2('Contrato', saleMode, this.handleSaleModeChange, SALE_MODES, lockType === 'STANDARD' ? ['revenue_share'] : [])}

          {saleMode === 'revenue_share' && (
            <div style={{ marginBottom: 14, padding: '14px 14px 10px', background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#7c3aed', display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#6d28d9' }}>Datos históricos del cliente</span>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ ...labelStyle, color: '#5b21b6' }}>Ventas últimos 3 años (€)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 4 }}>
                  {[{ lbl: 'Año -3', key: 'salesYear3', val: salesYear3 }, { lbl: 'Año -2', key: 'salesYear2', val: salesYear2 }, { lbl: 'Año -1', key: 'salesYear1', val: salesYear1 }].map(({ lbl, key, val }) => (
                    <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                      <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>{lbl}</span>
                      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #c4b5fd', borderRadius: 6, overflow: 'hidden', width: '100%' }}>
                        <input
                          type="number" min="0" step="100"
                          value={val}
                          placeholder="0"
                          onChange={(e) => this.setState({ [key]: e.target.value })}
                          style={{ flex: 1, border: 'none', padding: '6px 4px', fontSize: 12, textAlign: 'center', background: '#faf5ff', outline: 'none', width: 0 }}
                        />
                        <span style={{ padding: '0 6px', fontSize: 11, color: '#a78bfa', background: '#ede9fe', borderLeft: '1px solid #c4b5fd', lineHeight: '30px', flexShrink: 0 }}>€</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ ...labelStyle, color: '#5b21b6' }}>Taquillas existentes del cliente</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number" min="0" step="1"
                    value={existingLockers}
                    placeholder="0"
                    onChange={(e) => this.setState({ existingLockers: e.target.value })}
                    style={{ ...inputStyle, paddingRight: 60, background: '#faf5ff', border: '1px solid #c4b5fd' }}
                  />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#a78bfa' }}>uds.</span>
                </div>
              </div>
            </div>
          )}

          {(saleMode === 'rental' || saleMode === 'revenue_share') && (
            numField('Duración contrato', Math.round(contractDuration / 12), (e) => {
              const years = Math.max(1, parseInt(e.target.value) || 1);
              const update = { contractDuration: years * 12 };
              if (this.state.saleMode === 'revenue_share') {
                const cur = this.state.commissionPcts;
                update.commissionPcts = Array.from({ length: years }, (_, i) => cur[i] !== undefined ? cur[i] : 0);
              }
              this.setState(update);
            }, 'años')
          )}
          {saleMode === 'rental' && (
            numField('Meses de depósito', depositMonths, (e) => this.setState({ depositMonths: Math.max(0, parseInt(e.target.value) || 0) }), 'meses')
          )}
          {saleMode === 'revenue_share' && (() => {
            const tCalc = this.calculateTotals();
            const years = Math.round(contractDuration / 12);
            const pcts = Array.from({ length: years }, (_, i) => commissionPcts[i] !== undefined ? commissionPcts[i] : 0);
            return (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Comisión Cliente por año</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8, marginTop: 4 }}>
                    {pcts.map((pct, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Año {i + 1}</span>
                        <div style={{ display: 'flex', alignItems: 'center', border: `1px solid #e2e8f0`, borderRadius: 6, overflow: 'hidden', width: '100%' }}>
                          <input
                            type="number" min="0" max="100" step="0.5"
                            value={pct}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              const next = [...pcts];
                              next[i] = isNaN(v) ? 0 : Math.max(0, Math.min(100, v));
                              this.setState({ commissionPcts: next });
                            }}
                            style={{ flex: 1, border: 'none', padding: '6px 4px', fontSize: 12, textAlign: 'center', background: '#fafafa', outline: 'none', width: 0 }}
                          />
                          <span style={{ padding: '0 6px', fontSize: 12, color: '#64748b', background: '#f1f5f9', borderLeft: '1px solid #e2e8f0', lineHeight: '30px', flexShrink: 0 }}>%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {(() => {
                  const minimumAnnualCommission = tCalc.rentAnnual;
                  const s1 = parseFloat(salesYear1) || 0;
                  const s2 = parseFloat(salesYear2) || 0;
                  const s3 = parseFloat(salesYear3) || 0;
                  const hasInputs = salesYear1 !== '' || salesYear2 !== '' || salesYear3 !== '';
                  const avgSales = (s1 + s2 + s3) / 3;
                  const avgCommPct = pcts.length > 0 ? pcts.reduce((a, b) => a + b, 0) / pcts.length : 0;
                  const estimatedVacwayCommission = avgSales * avgCommPct / 100;

                  let status, bgColor, borderColor, textColor, statusMsg, actionMsg;
                  if (!hasInputs) {
                    status = 'RED';
                    bgColor = '#fef2f2'; borderColor = '#fca5a5'; textColor = '#991b1b';
                    statusMsg = 'Sin datos';
                    actionMsg = '⚠ Solo aprobado por dirección';
                  } else if (estimatedVacwayCommission >= minimumAnnualCommission * 1.10) {
                    status = 'GREEN';
                    bgColor = '#f0fdf4'; borderColor = '#86efac'; textColor = '#166534';
                    statusMsg = 'Viable con margen de seguridad';
                    actionMsg = 'El comercial puede avanzar';
                  } else if (estimatedVacwayCommission >= minimumAnnualCommission) {
                    status = 'AMBER';
                    bgColor = '#fffbeb'; borderColor = '#fcd34d'; textColor = '#92400e';
                    statusMsg = 'Viable, pero justo';
                    actionMsg = 'Permitir con aviso y revisión comercial';
                  } else {
                    status = 'RED';
                    bgColor = '#fef2f2'; borderColor = '#fca5a5'; textColor = '#991b1b';
                    statusMsg = 'No viable con la configuración actual';
                    actionMsg = 'Bloquear o requerir aprobación de dirección';
                  }

                  const dot = { width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                    background: status === 'GREEN' ? '#16a34a' : status === 'AMBER' ? '#d97706' : '#dc2626' };

                  return (
                    <div style={{ marginBottom: 14, padding: '10px 12px', background: bgColor, borderRadius: 8, border: `1px solid ${borderColor}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: hasInputs ? 6 : 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={dot} />
                          <span style={{ fontSize: 12, color: textColor, fontWeight: 700 }}>Comisión mínima anual (calculada)</span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: textColor }}>{fmtAlways(minimumAnnualCommission)} /año</span>
                      </div>
                      {hasInputs && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, color: textColor, fontStyle: 'italic' }}>{statusMsg} — {actionMsg}</span>
                          <span style={{ fontSize: 11, color: textColor, fontWeight: 600 }}>Est. {fmtAlways(estimatedVacwayCommission)} /año</span>
                        </div>
                      )}
                      {!hasInputs && (
                        <div style={{ fontSize: 11, color: textColor, marginTop: 4, fontStyle: 'italic' }}>{actionMsg}</div>
                      )}
                    </div>
                  );
                })()}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!this.state.hideMinAnnualCommission}
                    onChange={(e) => this.setState({ hideMinAnnualCommission: e.target.checked })}
                  />
                  Ocultar comisión mínima anual en resumen, vista previa y PDF
                </label>
              </>
            );
          })()}

          {numField('Instalación y puesta en marcha', installationCost, (e) => this.setState({ installationCost: parseFloat(e.target.value) || 0 }), '€')}

          <div style={sectionTitle}>Descuentos</div>
          {pctField('Descuento Producto', discountProduct, this.setDiscount('discountProduct', 100))}
          {saleMode !== 'revenue_share' && maintenanceType !== 'NONE' && pctField('Descuento Mantenimiento', discountMaintenance, this.setDiscount('discountMaintenance', 100))}
          {saleMode !== 'revenue_share' && licenseType !== 'NONE' && pctField('Descuento Licencias', discountLicense, this.setDiscount('discountLicense', 100))}

          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Condiciones</label>
            <textarea value={conditions} onChange={this.set('conditions')} rows={3}
              placeholder="Precio ex-works Cataluña. Transporte no incluido..."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>
      </div>

      {/* ── Recambios card ────────────────────────────────── */}
      {(() => {
        const { spareItems, saleMode } = this.state;
        const showSale = saleMode === 'purchase';
        const thS = { padding: '10px 8px', textAlign: 'right', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: TH_COLOR, background: TH_BG, borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap' };
        const tdS = { padding: '8px 8px', textAlign: 'right', borderBottom: `1px solid #f1f5f9`, fontSize: 13, color: TEXT_1 };
        return (
          <div style={{ ...cardStyle, marginTop: 20, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px 12px', borderBottom: `1px solid ${BORDER}` }}>
              <div style={sectionTitle}>Recambios</div>
              <button
                onClick={() => this.setState({ addProductModal: 'spare', addProductModalSearch: '' })}
                style={{ background: 'linear-gradient(135deg, #022040 0%, #0a4080 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Plus size={14} /> Añadir recambio
              </button>
            </div>
            {spareItems.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                  <thead>
                    <tr>
                      <th style={{ ...thS, textAlign: 'left', minWidth: 200 }}>Recambio</th>
                      <th style={{ ...thS, minWidth: 80 }}>Taquillas</th>
                      {showSale && <th style={{ ...thS, minWidth: 100 }}>Precio ud.</th>}
                      <th style={{ ...thS, minWidth: 70 }}>Cantidad</th>
                      {showSale && <th style={{ ...thS, minWidth: 100 }}>Total Venta</th>}
                      <th style={{ ...thS, minWidth: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {spareItems.map((it, idx) => (
                      <tr key={idx} style={{ background: ROW_HL }}>
                        <td style={{ ...tdS, textAlign: 'left', fontWeight: 600 }}>{it.name} <span style={{ fontSize: 10, color: TEXT_2, fontWeight: 400 }}>({it.code})</span></td>
                        <td style={{ ...tdS, fontWeight: 600 }}>{(it.lockers || 0) > 0 ? it.lockers : '—'}</td>
                        {showSale && <td style={tdS}>{fmtAlways(it.unitPrice)}</td>}
                        <td style={{ ...tdS, padding: '4px 4px' }}>
                          <input type="number" min="1" value={it.qty} onChange={this.setItemQty('spare', idx)}
                            style={{ width: 52, padding: '6px 4px', borderRadius: 6, border: `1px solid #0a4080`, textAlign: 'center', fontSize: 13, outline: 'none', background: '#fff' }} />
                        </td>
                        {showSale && <td style={{ ...tdS, fontWeight: 600, color: TEAL }}>{fmtAlways(it.qty * it.unitPrice)}</td>}
                        <td style={tdS}>
                          <button onClick={() => this.removeItem('spare', idx)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: RED, padding: 4 }}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={{ ...tdS, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>Subtotal</td>
                      <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{spareItems.reduce((s, it) => s + it.qty * (it.lockers || 0), 0) || '—'}</td>
                      {showSale && <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>}
                      <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{spareItems.reduce((s, it) => s + it.qty, 0)}</td>
                      {showSale && <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none', color: TEAL }}>{fmtAlways(spareItems.reduce((s, it) => s + it.qty * it.unitPrice, 0))}</td>}
                      <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {spareItems.length === 0 && (
              <div style={{ padding: '32px 24px', textAlign: 'center', color: TEXT_3, fontSize: 14 }}>
                Selecciona un recambio y pulsa "Añadir" para agregarlo a la propuesta
              </div>
            )}
          </div>
        );
      })()}
      </>
    );
  }

  // ─── Render: Product Adder + Table ─────────────────────
  renderProductSection(title, category, items, availableProducts) {
    const isExt = this.state.installationType === 'exterior';
    const { saleMode } = this.state;
    const showSale = saleMode === 'purchase';
    const showRent = saleMode === 'rental' || saleMode === 'revenue_share';
    const priceLabel = showSale ? (isExt ? 'Precio Ext.' : 'Precio Int.') : (isExt ? 'Alq./mes Ext.' : 'Alq./mes Int.');

    const thS = { padding: '10px 8px', textAlign: 'right', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: TH_COLOR, background: TH_BG, borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap' };
    const tdS = { padding: '8px 8px', textAlign: 'right', borderBottom: `1px solid #f1f5f9`, fontSize: 13, color: TEXT_1 };

    // Subtotals
    const sub = items.reduce((a, it) => ({
      units: a.units + it.qty,
      lockers: a.lockers + it.qty * (it.lockers || 0),
      sale: a.sale + it.qty * it.unitPrice,
      rent: a.rent + it.qty * (it.unitRent || 0),
    }), { units: 0, lockers: 0, sale: 0, rent: 0 });

    return (
      <div style={{ ...cardStyle, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px 12px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={sectionTitle}>{title}</div>
          {/* Add product button → opens modal */}
          <button
            onClick={() => this.setState({ addProductModal: category, addProductModalSearch: '' })}
            style={{ background: 'linear-gradient(135deg, #022040 0%, #0a4080 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Añadir producto
          </button>
        </div>

        {items.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr>
                  <th style={{ ...thS, textAlign: 'left', minWidth: 200 }}>Producto</th>
                  {showSale && <th style={{ ...thS, minWidth: 80 }}>{priceLabel}</th>}
                  <th style={{ ...thS, minWidth: 60 }}>Cantidad</th>
                  <th style={{ ...thS, minWidth: 60 }}>Taquillas</th>
                  {showSale && <th style={{ ...thS, minWidth: 85 }}>Total Venta</th>}
                  <th style={{ ...thS, minWidth: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} style={{ background: ROW_HL }}>
                    <td style={{ ...tdS, textAlign: 'left', fontWeight: 600 }}>
                      <div style={{ fontWeight: 700 }}>{it.envCode || it.code}</div>
                      {it.envCode && it.envCode !== it.code && <div style={{ fontSize: 10, color: TEXT_2, fontWeight: 400 }}>{it.code}</div>}
                    </td>
                    {showSale && <td style={tdS}>{fmtAlways(it.unitPrice)}</td>}
                    <td style={{ ...tdS, padding: '4px 4px' }}>
                      <input type="number" min="1" value={it.qty} onChange={this.setItemQty(category, idx)}
                        style={{ width: 52, padding: '6px 4px', borderRadius: 6, border: `1px solid #0a4080`, textAlign: 'center', fontSize: 13, outline: 'none', background: '#fff' }} />
                    </td>
                    <td style={{ ...tdS, fontWeight: 600 }}>{it.qty * it.lockers}</td>
                    {showSale && <td style={{ ...tdS, fontWeight: 600, color: TEAL }}>{fmtAlways(it.qty * it.unitPrice)}</td>}
                    <td style={tdS}>
                      <button onClick={() => this.removeItem(category, idx)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: RED, padding: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {/* Subtotal */}
                <tr style={{ background: '#f8fafc' }}>
                  <td style={{ ...tdS, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>Subtotal</td>
                  <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                  <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{sub.units}</td>
                  <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}>{sub.lockers}</td>
                  {showSale && <td style={{ ...tdS, fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none', color: TEAL }}>{fmtAlways(sub.sale)}</td>}
                  <td style={{ ...tdS, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' }}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {items.length === 0 && (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: TEXT_3, fontSize: 14 }}>
            Selecciona un producto y pulsa "Añadir" para agregar elementos a la propuesta
          </div>
        )}
      </div>
    );
  }

  // ─── Render: Grand Total ───────────────────────────────
  renderGrandTotal(t) {
    const { saleMode, licenseType, maintenanceType } = this.state;
    const c = { padding: '12px 16px', textAlign: 'center', fontWeight: 700, fontSize: 15, color: '#fff' };
    const lbl = { fontSize: 11, color: '#a8c8e8', marginBottom: 4, fontWeight: 400 };

    return (
      <div style={{ marginBottom: 20, background: `linear-gradient(135deg, #022040 0%, #0a4080 100%)`, borderRadius: 12, overflow: 'hidden', border: `1px solid #0a4080` }}>
        <div style={{ padding: '14px 24px', borderBottom: `1px solid rgba(255,255,255,0.15)` }}>
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#fff' }}>Resumen Económico</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: saleMode === 'purchase' ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)', gap: 0 }}>
          <div style={c}>
            <div style={lbl}>Módulos</div>
            {t.totalModules}
          </div>
          <div style={c}>
            <div style={lbl}>Taquillas</div>
            {t.totalLockers}
          </div>
          {saleMode === 'purchase' && (
            <div style={c}>
              <div style={lbl}>CAPEX (Venta)</div>
              {fmtAlways(t.sale)}
            </div>
          )}
          <div style={c}>
            <div style={{ ...lbl, color: GREEN }}>
              {saleMode === 'purchase' ? (licenseType === 'NONE' ? 'Licencia' : 'OPEX Licencias/año') : saleMode === 'rental' ? 'Alquiler/mes' : 'Revenue Share'}
            </div>
            <span style={{ color: GREEN }}>
              {saleMode === 'purchase'
                ? (licenseType === 'NONE' ? 'Sin Licencia' : fmtAlways(t.licenseAnnual))
                : saleMode === 'rental' ? fmtAlways(t.rentMonthly) : 'Revenue Share'}
            </span>
          </div>
          <div style={c}>
            <div style={{ ...lbl, color: AMBER }}>Personalización</div>
            <span style={{ color: AMBER }}>{fmtAlways(t.personalization)}</span>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Print Cover Page ──────────────────────────
  renderPrintCoverPage() {
    const { proposalRef, proposalDate, clientName, clientCompany, exportLang } = this.state;
    const lang = exportLang || 'es';
    const T = PDF_T[lang] || PDF_T['es'];
    const preparedBy = this.props.fullname || '';

    return (
      <div style={{
        width: '100%', height: 1018, display: 'flex', flexDirection: 'column',
        background: '#fff', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Background image */}
        <img
          src="/images/configurator/portada.png"
          alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
        />

        {/* Header with logo */}
        <div style={{ padding: '32px 48px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <img src="/images/logo_white.png" alt="VACWAYlockers" style={{ height: 36, width: 'auto' }} />
        </div>

        {/* Hero section — title centered in the upper-middle area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 60, position: 'relative', zIndex: 1 }}>
          {/* Line 1: OFERTA COMERCIAL */}
          <div style={{ fontSize: 56, fontWeight: 700, lineHeight: 1, letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center' }}>
            <span style={{ color: '#f59e0b' }}>{T.coverLine1}</span>
            <span style={{ color: '#ffffff' }}>{T.coverLine2}</span>
          </div>
          {/* Line 2: VACWAYlockers */}
          <div style={{ fontSize: 22, lineHeight: 1, letterSpacing: 4, textTransform: 'uppercase', textAlign: 'center', marginTop: 14 }}>
            <span style={{ color: '#ffffff', fontWeight: 300 }}>VACWAY</span>
            <span style={{ color: '#ffffff', fontWeight: 700, textTransform: 'none' }}>lockers</span>
          </div>
        </div>

        {/* Bottom info strip */}
        <div style={{ padding: '12px 48px', background: 'transparent', position: 'absolute', bottom: 30, left: 0, right: 0, zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>{T.preparedBy}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>{preparedBy || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>{T.offerRef}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>{proposalRef || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>{T.date}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>
                {proposalDate ? new Date(proposalDate + 'T00:00:00').toLocaleDateString(T.locale, { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  }

  // ─── Render: Print Header ──────────────────────────────
  // Shared header for print pages. `s` is a linear scale factor so the same
  // visual header can appear at a consistent physical size on both the narrow
  // portrait data page (s=1) and the wide landscape visual page (s≈2.75).
  renderPrintHeader(s = 1) {
    const { clientName, clientCompany, proposalRef, proposalDate, exportLang } = this.state;
    const lang = exportLang || 'es';
    const T = PDF_T[lang] || PDF_T['es'];
    const px = (n) => Math.round(n * s);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: px(20), borderBottom: `${Math.max(2, Math.round(3 * s))}px solid #0a4080`, paddingBottom: px(16) }}>
        <div>
          <img src="/images/logo_blue.png" alt="VACWAYlockers" style={{ height: px(32), width: 'auto', display: 'block' }} />
          <div style={{ fontSize: px(10), color: '#999', marginTop: px(2) }}>Smart Locker Solutions</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: px(11), color: '#666' }}>
          <div style={{ fontSize: px(16), fontWeight: 700, color: '#333', marginBottom: px(4) }}>{T.commercialProposal}</div>
          {proposalRef && <div>Ref: {proposalRef}</div>}
          <div>{T.dateLabel}{proposalDate}</div>
          {clientCompany && <div>{T.companyLabel}{clientCompany}</div>}
          {clientName && <div>{T.contactLabel}{clientName}</div>}
        </div>
      </div>
    );
  }

  // Company data + signature block (goes at the end of the visual page)
  renderPrintSignatureBlock(s = 1) {
    const { clientName, clientCompany, clientCIF, clientAddress, exportLang } = this.state;
    const lang = exportLang || 'es';
    const T = PDF_T[lang] || PDF_T['es'];
    const px = (n) => Math.round(n * s);
    const clientValues = [clientCompany, clientCIF, clientAddress, clientName];
    return (
      <div style={{ marginTop: px(20), pageBreakBefore: 'auto' }}>
        <div style={{ fontSize: px(11), color: '#0e7490', fontStyle: 'italic', marginBottom: px(14) }}>
          {T.acceptanceText}
        </div>

        <div style={{ fontSize: px(12), fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: px(10), display: 'flex', alignItems: 'center', gap: px(6) }}>
          <span style={{ color: '#0e7490' }}>➜</span> {T.companyDetails}
        </div>

        {T.sigFields.map(([lbl], i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: px(8) }}>
            <span style={{ width: px(200), fontSize: px(11), color: '#555' }}>{lbl}</span>
            <div style={{ flex: 1, borderBottom: '1px solid #ccc', minHeight: px(20), fontSize: px(11), paddingBottom: px(2) }}>{clientValues[i]}</div>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: px(30) }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: px(10), color: '#888', marginBottom: px(6) }}>{T.companyStamp}</div>
            <div style={{ width: px(200), height: px(80), border: '1px solid #ccc', borderRadius: px(6) }}></div>
            <div style={{ fontSize: px(10), color: '#888', marginTop: px(8) }}>{T.dateSignature}</div>
          </div>
        </div>
      </div>
    );
  }

  // Page 1 content: header + all proposal data (portrait, s=1)
  // Page 2 content: header + visual module distribution + signature block
  //                 (landscape, s≈2.75 so header/signature match portrait size)
  renderPrintVisualPage() {
    // Scale so text on the 2800px wide landscape container prints at the
    // same physical mm size as text on the 720px portrait container:
    //   portrait: 210mm / 720px  ≈ 0.2917 mm/px
    //   landscape: 297mm / 2800px ≈ 0.1061 mm/px
    //   ratio ≈ 2.75
    const s = 2.75;
    return (
      <div style={{ color: '#333', lineHeight: 1.5 }}>
        {this.renderPrintHeader(s)}
        {this.renderPrintVisualLine()}
        {this.renderPrintSignatureBlock(s)}
      </div>
    );
  }

  renderPrintDataPage(t, includeSignature = false) {
    const { notes,
            licenseType, maintenanceType, customization, installationType,
            lockType, saleMode, contractDuration, depositMonths,
            commissionPcts, installationCost, conditions,
            discountProduct, discountMaintenance, discountLicense, discountRental,
            pinRfidType, operatingMode, onlineSale,
            cabinetItems, posItems, spareItems, exportLang } = this.state;

    const lang = exportLang || 'es';
    const T = PDF_T[lang] || PDF_T['es'];
    const instLbl = INSTALLATION_TYPES.find(i => i.value === installationType)?.label || '';
    const lockLbl = LOCK_TYPES.find(l => l.value === lockType)?.label || '';
    const modeLbl = T.saleMode[saleMode] || saleMode;
    const maintLbl = T.maintType[maintenanceType] || maintenanceType;

    const thP = { padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontSize: 9, textTransform: 'uppercase', color: '#0e7490', background: '#e0f7fa', borderBottom: '2px solid #b2ebf2', whiteSpace: 'nowrap' };
    const tdP = { padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #e8e8e8', fontSize: 11, color: '#333' };

    const showSale = saleMode === 'purchase';

    const renderPrintTable = (title, items) => {
      if (items.length === 0) return null;
      const sub = items.reduce((a, it) => ({
        units: a.units + it.qty, lockers: a.lockers + it.qty * it.lockers,
        sale: a.sale + it.qty * it.unitPrice, rent: a.rent + it.qty * it.unitRent,
      }), { units: 0, lockers: 0, sale: 0, rent: 0 });

      return (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#0e7490' }}>➜</span> {title}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thP, textAlign: 'left', minWidth: 90, width: 90 }}>{T.code}</th>
                <th style={{ ...thP, textAlign: 'left', minWidth: 160 }}>{T.description}</th>
                <th style={thP}>{T.quantity}</th>
                <th style={thP}>{T.lockers}</th>
                {showSale && <th style={thP}>{T.unitPrice}</th>}
                {showSale && <th style={thP}>{T.totalSale}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? '#f0f9ff' : '#fff' }}>
                  <td style={{ ...tdP, textAlign: 'left', fontWeight: 600, fontSize: 9, color: '#64748b', whiteSpace: 'nowrap' }}>
                    <div>{it.code}</div>
                    {it.envCode && it.envCode !== it.code && <div style={{ fontWeight: 400, color: '#94a3b8' }}>{it.envCode}</div>}
                  </td>
                  <td style={{ ...tdP, textAlign: 'left', fontWeight: 600 }}>
                    {it.name.split('/').map((part, i) => <div key={i} style={{ fontSize: 10, color: '#333', fontWeight: i === 0 ? 600 : 400 }}>{part.trim()}</div>)}
                  </td>
                  <td style={{ ...tdP, fontWeight: 600 }}>{it.qty}</td>
                  <td style={tdP}>{it.qty * it.lockers}</td>
                  {showSale && <td style={tdP}>{fmtAlways(it.unitPrice)}</td>}
                  {showSale && <td style={{ ...tdP, fontWeight: 600 }}>{fmtAlways(it.qty * it.unitPrice)}</td>}
                </tr>
              ))}
              <tr style={{ background: '#f8f8f8' }}>
                <td style={{ ...tdP, textAlign: 'left', fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none' }} colSpan={2}>{T.subtotal}</td>
                <td style={{ ...tdP, fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none' }}>{sub.units}</td>
                <td style={{ ...tdP, fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none' }}>{sub.lockers}</td>
                {showSale && <td style={{ ...tdP, borderTop: '2px solid #ccc', borderBottom: 'none' }}></td>}
                {showSale && <td style={{ ...tdP, fontWeight: 700, borderTop: '2px solid #ccc', borderBottom: 'none', color: '#0e7490' }}>{fmtAlways(sub.sale)}</td>}
              </tr>
            </tbody>
          </table>
        </div>
      );
    };

    const printKv = (label, value, bold) => (
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', background: bold ? '#f0f9ff' : undefined }}>
        <div style={{ width: 16, padding: '5px 0 5px 10px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <span style={{ color: '#0e7490', fontSize: 11 }}>➜</span>
        </div>
        <div style={{ flex: 1, padding: '5px 8px', fontSize: 11, color: bold ? '#0e7490' : '#555', fontWeight: bold ? 700 : 400 }}>{label}</div>
        <div style={{ width: 120, padding: '5px 10px', fontSize: 11, fontWeight: bold ? 700 : 600, color: bold ? '#0e7490' : '#333', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{value}</div>
      </div>
    );

    const printKvDiscount = (label, baseValue, discountedValue, discountPct) => {
      if (!discountPct || discountPct <= 0) return printKv(label, fmtAlways(discountedValue));
      const saving = discountedValue - baseValue;
      return (
        <>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
            <div style={{ width: 16, padding: '5px 0 5px 10px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <span style={{ color: '#0e7490', fontSize: 11 }}>➜</span>
            </div>
            <div style={{ flex: 1, padding: '5px 8px', fontSize: 11, color: '#555' }}>{label}</div>
            <div style={{ width: 120, padding: '5px 10px', fontSize: 11, fontWeight: 600, color: '#333', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(baseValue)}</div>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee', background: '#fffbeb' }}>
            <div style={{ width: 16, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: '3px 8px', fontSize: 10, color: '#b45309' }}>{T.disc}{discountPct}%</div>
            <div style={{ width: 120, padding: '3px 10px', fontSize: 10, fontWeight: 600, color: '#b45309', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(saving)}</div>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
            <div style={{ width: 16, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: '3px 8px 5px', fontSize: 10, color: '#555' }}>{T.total}</div>
            <div style={{ width: 120, padding: '3px 10px 5px', fontSize: 11, fontWeight: 700, color: '#0e7490', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(discountedValue)}</div>
          </div>
        </>
      );
    };

    const printSectionHdr = (label) => (
      <div style={{ fontSize: 12, fontWeight: 700, color: '#333', margin: '14px 0 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#333', display: 'inline-block' }} />
        {label}
      </div>
    );

    return (
      <div style={{ color: '#333', lineHeight: 1.5 }}>
        {this.renderPrintHeader()}

        {/* ── Section: Installation Proposal ── */}
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 12, letterSpacing: 0.5 }}>
          {T.installationProposal}
        </div>

        {/* Config summary grid */}
        {(() => {
          const opModeLbl = T.opMode[operatingMode] || operatingMode || '';
          const pinLbl    = LOCK_ACCESS_TYPES.find(m => m.value === pinRfidType)?.label || pinRfidType || '';
          const cfgItems = T.cfgLabels.map((label, i) => {
            const values = [
              t.totalModules, t.totalLockers, instLbl, lockLbl, pinLbl, opModeLbl,
              onlineSale ? T.yesVal : T.noVal,
              T.saleMode[saleMode] || saleMode,
              licenseType === 'NONE' ? T.noLicense : (T.licenseType[licenseType] || licenseType),
              T.maintType[maintenanceType] || maintenanceType,
              customization ? T.yesVal : T.noVal,
            ];
            return { label, value: values[i] };
          });
          // Split into two columns
          const half = Math.ceil(cfgItems.length / 2);
          const col1 = cfgItems.slice(0, half);
          const col2 = cfgItems.slice(half);
          const cellLbl = { padding: '5px 8px', fontSize: 10, color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9', background: '#f8fafc', whiteSpace: 'nowrap' };
          const cellVal = { padding: '5px 10px', fontSize: 10, color: '#1e293b', fontWeight: 700, borderBottom: '1px solid #f1f5f9', borderLeft: '1px solid #e2e8f0' };
          const renderCol = (rows) => (
            <table style={{ flex: 1, borderCollapse: 'collapse', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={cellLbl}>{r.label}</td>
                    <td style={cellVal}>{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
          return (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {renderCol(col1)}
              {renderCol(col2)}
            </div>
          );
        })()}

        {renderPrintTable(T.lockerCabinets, cabinetItems)}
        {renderPrintTable(T.pointsOfSale, posItems)}
        {spareItems && spareItems.length > 0 && renderPrintTable(T.spareParts, spareItems)}

      </div>
    );
  }

  renderPrintOfertaPage(t, includeSignature = false) {
    const { notes,
            licenseType, maintenanceType, customization, installationType,
            saleMode, contractDuration, depositMonths,
            commissionPcts, installationCost, conditions,
            discountProduct, discountMaintenance, discountLicense, discountRental,
            exportLang } = this.state;

    const lang = exportLang || 'es';
    const T = PDF_T[lang] || PDF_T['es'];
    const instLbl  = INSTALLATION_TYPES.find(i => i.value === installationType)?.label || '';
    const modeLbl  = T.saleMode[saleMode] || saleMode;
    const maintLbl = T.maintType[maintenanceType] || maintenanceType;
    const perYear  = T.perYear;
    const perMonth = T.perMonth;
    const notIncl  = T.notIncluded;
    const pending  = T.pending;
    const included = T.included;
    const yrsVal   = (months) => Math.round(months / 12) + T.yearsUnit;

    const printKv = (label, value, bold) => (
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', background: bold ? '#f0f9ff' : undefined }}>
        <div style={{ width: 16, padding: '5px 0 5px 10px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <span style={{ color: '#0e7490', fontSize: 11 }}>➜</span>
        </div>
        <div style={{ flex: 1, padding: '5px 8px', fontSize: 11, color: bold ? '#0e7490' : '#555', fontWeight: bold ? 700 : 400 }}>{label}</div>
        <div style={{ width: 120, padding: '5px 10px', fontSize: 11, fontWeight: bold ? 700 : 600, color: bold ? '#0e7490' : '#333', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{value}</div>
      </div>
    );

    const printKvDiscount = (label, baseValue, discountedValue, discountPct, suffix = '') => {
      if (!discountPct || discountPct <= 0) return printKv(label, fmtAlways(discountedValue) + suffix);
      const saving = discountedValue - baseValue;
      return (
        <>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
            <div style={{ width: 16, padding: '5px 0 5px 10px', flexShrink: 0, display: 'flex', alignItems: 'center' }}><span style={{ color: '#0e7490', fontSize: 11 }}>➜</span></div>
            <div style={{ flex: 1, padding: '5px 8px', fontSize: 11, color: '#555' }}>{label}</div>
            <div style={{ width: 120, padding: '5px 10px', fontSize: 11, fontWeight: 600, color: '#333', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(baseValue)}{suffix}</div>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee', background: '#fffbeb' }}>
            <div style={{ width: 16, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: '3px 8px 5px', fontSize: 10, color: '#b45309' }}>{T.disc}{discountPct}%</div>
            <div style={{ width: 120, padding: '3px 10px 5px', fontSize: 11, fontWeight: 600, color: '#b45309', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(saving)}</div>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
            <div style={{ width: 16, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: '3px 8px 5px', fontSize: 10, color: '#555' }}>{T.total}</div>
            <div style={{ width: 120, padding: '3px 10px 5px', fontSize: 11, fontWeight: 700, color: '#0e7490', borderLeft: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>{fmtAlways(discountedValue)}{suffix}</div>
          </div>
        </>
      );
    };

    const printSectionHdr = (label) => (
      <div style={{ fontSize: 12, fontWeight: 700, color: '#333', margin: '14px 0 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#333', display: 'inline-block' }} />
        {label}
      </div>
    );

    return (
      <div style={{ color: '#333', lineHeight: 1.5 }}>
        {this.renderPrintHeader()}

        {/* ── Section: CAPEX / OPEX ── */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 0.5 }}>
            {T.economicOffer}
          </div>
          <div style={{ background: '#0e7490', color: '#fff', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, marginBottom: 10, display: 'inline-block' }}>
            {modeLbl}
          </div>

          {saleMode === 'purchase' && (
            <div style={{ overflow: 'hidden', marginBottom: 16 }}>
              {printSectionHdr('CAPEX')}
              {printKvDiscount(T.lockersLabel + instLbl + ')', t.baseSale, t.sale, discountProduct)}
              {t.spareSale > 0 && printKv(T.sparePartsLbl, fmtAlways(t.spareSale))}
              {t.consumableSale > 0 && printKv(T.consumables, fmtAlways(t.consumableSale))}
              {printKv(T.customization, customization ? fmtAlways(t.personalization) : notIncl)}
              {printKv(T.installationLabel, installationCost > 0 ? fmtAlways(installationCost) : pending)}
              {printKv('TOTAL CAPEX', fmtAlways(t.sale + (t.spareSale || 0) + (t.consumableSale || 0) + (customization ? t.personalization : 0) + (installationCost > 0 ? installationCost : 0)), true)}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printSectionHdr('OPEX')}
              {licenseType !== 'NONE' && printKvDiscount(T.licensesLabel + licenseType + ')', t.baseLicenseMonth * 12, t.licenseAnnual, discountLicense, perYear)}
              {maintenanceType !== 'NONE' && printKvDiscount(T.maintenanceLabel + maintLbl + ')', t.baseMaintYear, t.maintenanceAnnual, discountMaintenance, perYear)}
              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printKv('TOTAL OPEX', fmtAlways((licenseType !== 'NONE' ? t.licenseAnnual : 0) + (maintenanceType !== 'NONE' ? t.maintenanceAnnual : 0)) + perYear, true)}
            </div>
          )}

          {saleMode === 'rental' && (
            <div style={{ marginBottom: 16 }}>
              {printSectionHdr(T.rental)}
              {t.rentalDiscountPct > 0 && printKv(T.basePrice, fmtAlways(t.baseRentAnnual) + perYear)}
              {t.rentalDiscountPct > 0 && printKv(T.discountApplied, '-' + t.rentalDiscountPct + '%')}
              {printKv(T.annualRentalLabel + instLbl + ')', fmtAlways(t.rentAnnual) + perYear, true)}
              {printKv(T.monthlyRentalLabel + instLbl + ')', fmtAlways(t.rentMonthly) + perMonth, true)}
              {printKv(T.depositLabel(depositMonths), fmtAlways(t.deposit))}
              {printKv(T.contractMandatory, yrsVal(contractDuration))}
              {t.spareSale > 0 && printKv(T.sparePartsRental, fmtAlways(t.spareSale))}
              {t.consumableSale > 0 && printKv(T.consumablesOneOff, fmtAlways(t.consumableSale))}
              {printKv(T.customization, customization ? fmtAlways(t.personalization) : notIncl)}
              {printKv(T.installationLabel, installationCost > 0 ? fmtAlways(installationCost) : pending)}
            </div>
          )}

          {saleMode === 'revenue_share' && (
            <div style={{ marginBottom: 16 }}>
              {printSectionHdr('REVENUE SHARE')}
              {printKv(T.contractDuration, yrsVal(contractDuration))}
              {commissionPcts.map((pct, i) => printKv(T.commissionYear(i + 1), fmtPct(pct)))}
              {!this.state.hideMinAnnualCommission && printKv(T.minAnnualCommission, fmtAlways(t.rentAnnual) + perYear, true)}
              {t.consumableSale > 0 && printKv(T.consumablesOneOff, fmtAlways(t.consumableSale))}
              {printKv(T.customization, customization ? fmtAlways(t.personalization) : notIncl)}
              {printKv(T.installationLabel, installationCost > 0 ? fmtAlways(installationCost) : pending)}

              {(licenseType !== 'NONE' || maintenanceType !== 'NONE') && printSectionHdr('OPEX')}
              {licenseType !== 'NONE' && printKv(T.licenses, T.includedF)}
              {maintenanceType !== 'NONE' && printKv(T.maintenance, included)}
            </div>
          )}
        </div>

        {/* Conditions */}
        {conditions && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#333', textTransform: 'uppercase', marginBottom: 6 }}>{T.conditions}</div>
            <div style={{ fontSize: 10, color: '#555', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{conditions}</div>
          </div>
        )}

        {/* Notes */}
        {notes && (
          <div style={{ marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', marginBottom: 6 }}>{T.notes}</div>
            <div style={{ fontSize: 11, color: '#555', whiteSpace: 'pre-wrap' }}>{notes}</div>
          </div>
        )}

        {includeSignature && this.renderPrintSignatureBlock()}
      </div>
    );
  }

  renderPrintContent(t) {
    const { moduleMode } = this.state;
    // On-screen preview shows both printed pages stacked vertically.
    // In visual mode: cover + data page + visual distribution + signature.
    // In list mode: cover + data page (with signature) only.
    return (
      <>
        {this.renderPrintCoverPage()}
        <div style={{ height: 32, margin: '24px 0', borderTop: '1px dashed #cbd5e1' }} />
        {this.renderPrintDataPage(t)}
        <div style={{ height: 32, margin: '24px 0', borderTop: '1px dashed #cbd5e1' }} />
        {this.renderPrintOfertaPage(t, moduleMode === 'list')}
        {moduleMode === 'visual' && (
          <>
            <div style={{ height: 32, margin: '24px 0', borderTop: '1px dashed #cbd5e1' }} />
            {this.renderPrintVisualPage()}
          </>
        )}
      </>
    );
  }

  // ─── Render: Rentability Block ──────────────────────────
  renderRentabilityBlock(t) {
    // Inputs are read from the proposal. Only assetPurchaseCost is manual.
    const LICENSE_COST_PER_LOCKER_YEAR     = this.getSetting('internalLicenseCostPerLockerYear', 1.25);
    const MAINTENANCE_COST_PER_LOCKER_YEAR = this.getSetting('internalMaintCostPerLockerYear', 3.50);
    const ANNUAL_INTEREST_RATE             = 0.05;

    const safePct = (num, den) => (!den || den === 0) ? null : num / den;
    const fmtPctR = (v) => v === null ? 'N/A' : `${(v * 100).toFixed(2).replace('.', ',')}%`;
    const round2  = (n) => Math.round(n * 100) / 100;

    const { rentab_model: model } = this.state;

    // ── Values auto-derived from the proposal (read-only) ──
    const numLockers         = t.totalLockers;
    const assetSalePrice     = t.sale + (t.spareSale || 0);
    const softwareRevYear    = t.licenseAnnual;
    const maintRevYear       = t.maintenanceAnnual;
    const signedMonthlyFee   = t.rentMonthly;
    const rentabInstallCost  = parseFloat(this.state.installationCost) || 0;
    const contractMonths     = this.state.contractDuration || 0;
    const minAnnualGuarantee = t.rentAnnual;

    // ── Internal asset cost: pick interior/exterior per item based on proposal installationType ──
    const isExt = this.state.installationType === 'exterior';
    const assetPurchaseCost = [...this.state.cabinetItems, ...this.state.posItems, ...(this.state.spareItems || [])].reduce((sum, it) => {
      const prod = (this.props.products || []).find(p => p._id === it.productId);
      const cost = prod ? (isExt ? prod.assetCostExterior || 0 : prod.assetCostInterior || 0) : 0;
      return sum + cost * it.qty;
    }, 0);

    // ── Personalization: revenue from proposal, cost from catalog ──
    // Both are zero when customization is not selected (NO)
    const personalizationRevenue = this.state.customization ? t.personalization : 0;
    const personalizationCostTotal = this.state.customization
      ? [...this.state.cabinetItems, ...this.state.posItems].reduce((sum, it) => {
          const prod = (this.props.products || []).find(p => p._id === it.productId);
          return sum + (prod ? (prod.personalizationCost || 0) : 0) * it.qty;
        }, 0)
      : 0;

    // ── OPEX (always calculated) ────────────────────────
    // annualLicenseCost is 0 when license is NONE or there are no POS items (softwareRevYear already 0 in both cases)
    const annualLicenseCost = softwareRevYear > 0 ? numLockers * LICENSE_COST_PER_LOCKER_YEAR : 0;
    // annualMaintCost is 0 when maintenanceType is NONE (maintRevYear already 0 in that case)
    const annualMaintCost   = maintRevYear > 0 ? numLockers * MAINTENANCE_COST_PER_LOCKER_YEAR : 0;
    const annualOpexCost    = annualLicenseCost + annualMaintCost;
    const monthlyOpexCost   = annualOpexCost / 12;

    // ── CaixaBank Renting (rental & revenue-share only) ─
    // Uses assetPurchaseCost (internal cost of selected modules) as the financed principal
    let caixabankMonthlyFee = 0;
    let rentingDetails      = null;
    if ((model === 'rental' || model === 'revenue_share') && contractMonths > 0) {
      const contractYears          = contractMonths / 12;
      const financedWithInterest   = assetPurchaseCost * (1 + ANNUAL_INTEREST_RATE * contractYears);
      const baseMonthlyFee         = financedWithInterest / contractMonths;
      const finalPurchaseQuota     = baseMonthlyFee;
      const totalCaixabankCost     = financedWithInterest + finalPurchaseQuota;
      caixabankMonthlyFee          = totalCaixabankCost / contractMonths;
      rentingDetails = { contractYears, financedWithInterest, baseMonthlyFee, finalPurchaseQuota, totalCaixabankCost, caixabankMonthlyFee };
    }

    // ── Sale calculations ────────────────────────────────
    const saleGrossMarginAmount  = assetSalePrice - assetPurchaseCost;
    const saleGrossMarginPct     = safePct(saleGrossMarginAmount, assetSalePrice);
    const licenseMarginAmount    = softwareRevYear - annualLicenseCost;
    const licenseMarginPct       = safePct(licenseMarginAmount, softwareRevYear);
    const maintMarginAmount      = maintRevYear - annualMaintCost;
    const maintMarginPct         = safePct(maintMarginAmount, maintRevYear);
    const annualOpexRevenue      = softwareRevYear + maintRevYear;
    const annualOpexMargin       = annualOpexRevenue - annualOpexCost;
    const annualOpexMarginPct    = safePct(annualOpexMargin, annualOpexRevenue);

    // Personalization margin
    const personalizationMarginAmount = personalizationRevenue - personalizationCostTotal;
    const personalizationMarginPct    = safePct(personalizationMarginAmount, personalizationRevenue);

    // Combined gross margin (asset + personalization)
    const totalGrossRevenue      = assetSalePrice + personalizationRevenue;
    const totalGrossMarginAmount = saleGrossMarginAmount + personalizationMarginAmount;
    const totalGrossMarginPct    = safePct(totalGrossMarginAmount, totalGrossRevenue);

    // ── Rental calculations ──────────────────────────────
    const monthlyRentalMargin    = signedMonthlyFee - caixabankMonthlyFee - monthlyOpexCost;
    const monthlyRentalMarginPct = safePct(monthlyRentalMargin, signedMonthlyFee);
    const totalRentalRevenue     = signedMonthlyFee * contractMonths;
    const totalRentalCost        = (caixabankMonthlyFee + monthlyOpexCost) * contractMonths;
    const totalRentalMargin      = totalRentalRevenue - totalRentalCost;
    const totalRentalMarginPct   = safePct(totalRentalMargin, totalRentalRevenue);

    // ── Revenue-share calculations ───────────────────────
    const minimumMonthlyGuarantee  = minAnnualGuarantee / 12;
    const monthlyRSMargin          = minimumMonthlyGuarantee - caixabankMonthlyFee - monthlyOpexCost;
    const monthlyRSMarginPct       = safePct(monthlyRSMargin, minimumMonthlyGuarantee);
    const totalRSRevenue           = minimumMonthlyGuarantee * contractMonths;
    const totalRSCost              = (caixabankMonthlyFee + monthlyOpexCost) * contractMonths;
    const totalRSMargin            = totalRSRevenue - totalRSCost;
    const totalRSMarginPct         = safePct(totalRSMargin, totalRSRevenue);

    // ── Financial analysis: TIR, VAN, Payback ────────────
    const calcFinancials = (investment, monthlyMargin) => {
      if (!investment || investment <= 0 || contractMonths <= 0) return { van: null, tirAnnual: null, paybackYears: null };
      const mdr = Math.pow(1 + ANNUAL_INTEREST_RATE, 1 / 12) - 1;
      let npv = -investment;
      for (let m = 1; m <= contractMonths; m++) npv += monthlyMargin / Math.pow(1 + mdr, m);
      let tirAnnual = null;
      if (monthlyMargin > 0) {
        let rate = 0.01;
        for (let i = 0; i < 200; i++) {
          let f = -investment, df = 0;
          for (let t = 1; t <= contractMonths; t++) {
            const pw = Math.pow(1 + rate, t);
            f  += monthlyMargin / pw;
            df -= t * monthlyMargin / (pw * (1 + rate));
          }
          if (Math.abs(df) < 1e-10) break;
          const nr = rate - f / df;
          if (!isFinite(nr) || nr <= -1) break;
          if (Math.abs(nr - rate) < 1e-10) { rate = nr; break; }
          rate = nr;
        }
        if (isFinite(rate) && rate > -1) tirAnnual = Math.pow(1 + rate, 12) - 1;
      }
      let paybackYears = null;
      if (monthlyMargin > 0) {
        let cum = -investment;
        for (let m = 1; m <= contractMonths; m++) {
          cum += monthlyMargin;
          if (cum >= 0) { paybackYears = m / 12; break; }
        }
      }
      return { van: Math.round(npv), tirAnnual, paybackYears };
    };
    // Perspective 1: Coste del activo — flujo neto tras CaixaBank + OPEX
    const finMargin1 = model === 'rental' ? monthlyRentalMargin : monthlyRSMargin;
    const fin1 = (model === 'rental' || model === 'revenue_share') && contractMonths > 0
      ? calcFinancials(assetPurchaseCost, finMargin1)
      : { van: null, tirAnnual: null, paybackYears: null };
    // Perspective 2: Coste total CaixaBank — flujo bruto antes de cuota CaixaBank (sólo OPEX deducido)
    const grossMonthly2     = (model === 'rental' ? signedMonthlyFee : minimumMonthlyGuarantee) - monthlyOpexCost;
    const caixabankTotalCost = rentingDetails ? rentingDetails.totalCaixabankCost : 0;
    const fin2 = (model === 'rental' || model === 'revenue_share') && contractMonths > 0 && caixabankTotalCost > 0
      ? calcFinancials(caixabankTotalCost, grossMonthly2)
      : { van: null, tirAnnual: null, paybackYears: null };

    // ── Semaphore ────────────────────────────────────────
    const mainMarginPct =
      model === 'sale'          ? totalGrossMarginPct :
      model === 'rental'        ? monthlyRentalMarginPct :
      /* revenue_share */         monthlyRSMarginPct;

    const semColor =
      mainMarginPct === null       ? TEXT_3 :
      mainMarginPct > 0.25         ? GREEN  :
      mainMarginPct >= 0.10        ? AMBER  :
      /* < 10% */                    RED;

    const semLabel =
      mainMarginPct === null  ? 'Sin datos suficientes para calcular' :
      mainMarginPct > 0.25    ? 'Oferta saludable' :
      mainMarginPct >= 0.10   ? 'Oferta aceptable — revisar condiciones' :
      /* < 10% */               'Oferta con poco margen o riesgo económico';

    const semEmoji = semColor === GREEN ? '✓' : semColor === AMBER ? '!' : semColor === RED ? '✗' : '–';

    // ── Validation notices ───────────────────────────────
    const errors = [];
    if (numLockers === 0)
      errors.push('Añade taquillas a la propuesta para calcular la rentabilidad.');
    if ((model === 'rental' || model === 'revenue_share') && contractMonths === 0)
      errors.push('Configura la duración del contrato en la sección «Tipo de Contrato».');
    if (model === 'sale' && assetSalePrice === 0)
      errors.push('Precio de venta es 0 — el margen % no se puede calcular.');
    if (model === 'rental' && signedMonthlyFee === 0)
      errors.push('Cuota mensual calculada es 0 — añade productos a la propuesta.');
    if (model === 'revenue_share' && minAnnualGuarantee === 0)
      errors.push('Comisión mínima anual es 0 — configura los porcentajes de comisión.');

    const canShowRentalResults = contractMonths > 0;

    // ── Sub-components ───────────────────────────────────
    const derivedRow = (label, value) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontSize: 11, color: TEXT_2 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_1 }}>{value}</span>
      </div>
    );

    const row = (label, value) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontSize: 12, color: TEXT_2 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_1 }}>{value}</span>
      </div>
    );

    const rowMargin = (label, eur, pct, isTotal = false) => {
      const pctColor = pct === null ? TEXT_3 : pct > 0.25 ? GREEN : pct >= 0.10 ? AMBER : RED;
      return (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '6px 0', borderBottom: `1px solid ${BORDER}`,
          background: isTotal ? `${pctColor}12` : undefined,
        }}>
          <span style={{ fontSize: 12, color: TEXT_2, fontWeight: isTotal ? 700 : 400 }}>{label}</span>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: eur > 0 ? GREEN : eur < 0 ? RED : TEXT_2 }}>
              {fmtAlways(round2(eur))}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: pctColor, minWidth: 64, textAlign: 'right' }}>
              {fmtPctR(pct)}
            </span>
          </div>
        </div>
      );
    };

    const resultCard = (title, children) => (
      <div style={{ background: '#fafbfc', borderRadius: 8, padding: '14px 16px', border: `1px solid ${BORDER}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
          {title}
        </div>
        {children}
      </div>
    );

    const MODEL_NAMES = { sale: 'Venta', rental: 'Alquiler', revenue_share: 'Revenue-share' };

    return (
      <div style={{ ...cardStyle, marginTop: 24, borderTop: `3px solid ${TEAL}` }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: TEAL, textTransform: 'uppercase', letterSpacing: 1 }}>
              Análisis de Rentabilidad Interna
            </div>
            <div style={{ fontSize: 11, color: TEXT_3, marginTop: 3 }}>
              Cálculos independientes — solo para uso interno, no se incluyen en la propuesta comercial
            </div>
          </div>
          {/* Model selector */}
          <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
            {['sale', 'rental', 'revenue_share'].map((m, idx) => (
              <button key={m} type="button"
                onClick={() => this.setState({ rentab_model: m })}
                style={{
                  padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  borderLeft: idx > 0 ? `1px solid ${BORDER}` : 'none',
                  borderTop: 'none', borderRight: 'none', borderBottom: 'none',
                  background: model === m ? 'linear-gradient(135deg, #022040 0%, #0a4080 100%)' : '#fff',
                  color: model === m ? '#fff' : TEXT_2,
                  transition: 'all 0.15s',
                }}>
                {MODEL_NAMES[m]}
              </button>
            ))}
          </div>
        </div>

        {/* ── Validation notices ── */}
        {errors.length > 0 && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', borderRadius: 8, border: `1px solid ${AMBER}60` }}>
            {errors.map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: AMBER, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>⚠</span> {e}
              </div>
            ))}
          </div>
        )}

        {/* ── Main grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 300px) 1fr', gap: 24, alignItems: 'start' }}>

          {/* LEFT: Read-only proposal values + computed boxes */}
          <div style={{ background: '#fafbfc', borderRadius: 8, padding: 16, border: `1px solid ${BORDER}` }}>
            <div style={{ ...sectionTitle, marginBottom: 6 }}>Datos de la Propuesta</div>
            <div style={{ fontSize: 10, color: TEXT_3, marginBottom: 12 }}>Valores extraídos automáticamente</div>

            {derivedRow('Taquillas totales', numLockers)}

            {model === 'sale' && (<>
              {derivedRow('Precio de venta (con descuento)', fmtAlways(assetSalePrice))}
              {derivedRow('Licencias cobradas /año', fmtAlways(softwareRevYear))}
              {derivedRow('Mantenimiento cobrado /año', fmtAlways(maintRevYear))}
              {derivedRow('Coste interno del activo', fmtAlways(assetPurchaseCost))}
              {derivedRow('Personalización (ingresos)', fmtAlways(personalizationRevenue))}
              {derivedRow('Personalización (coste interno)', fmtAlways(personalizationCostTotal))}
            </>)}

            {model === 'rental' && (<>
              {derivedRow('Cuota mensual cliente', fmtAlways(signedMonthlyFee) + ' /mes')}
              {derivedRow('Coste activo a financiar', assetPurchaseCost > 0 ? fmtAlways(assetPurchaseCost) : 'Sin coste activo definido')}
              {derivedRow('Duración contrato', contractMonths + ' meses')}
            </>)}

            {model === 'revenue_share' && (<>
              {derivedRow('Comisión mínima anual', fmtAlways(minAnnualGuarantee) + ' /año')}
              {derivedRow('Coste activo a financiar', assetPurchaseCost > 0 ? fmtAlways(assetPurchaseCost) : 'Sin coste activo definido')}
              {derivedRow('Duración contrato', contractMonths + ' meses')}
            </>)}

            {/* OPEX computed info */}
            <div style={{ marginTop: 14, padding: '10px 12px', background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                OPEX Interno (calculado)
              </div>
              <div style={{ fontSize: 11, color: TEXT_2, display: 'flex', justifyContent: 'space-between' }}>
                <span>Licencias ({numLockers} × {LICENSE_COST_PER_LOCKER_YEAR.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €)</span>
                <span style={{ fontWeight: 600 }}>{fmtAlways(annualLicenseCost)} /año</span>
              </div>
              <div style={{ fontSize: 11, color: TEXT_2, display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                <span>Mantenimiento ({numLockers} × {MAINTENANCE_COST_PER_LOCKER_YEAR.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €)</span>
                <span style={{ fontWeight: 600 }}>{fmtAlways(annualMaintCost)} /año</span>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', display: 'flex', justifyContent: 'space-between', marginTop: 5, borderTop: '1px solid #bae6fd', paddingTop: 4 }}>
                <span>OPEX total</span>
                <span>{fmtAlways(annualOpexCost)} /año &nbsp;|&nbsp; {fmtAlways(round2(monthlyOpexCost))} /mes</span>
              </div>
            </div>

            {/* CaixaBank computed info (rental / RS) */}
            {rentingDetails && (
              <div style={{ marginTop: 8, padding: '10px 12px', background: '#fffbeb', borderRadius: 6, border: '1px solid #fde68a' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  CaixaBank Renting (calculado)
                </div>
                <div style={{ fontSize: 11, color: TEXT_2, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Años contrato</span>
                  <span style={{ fontWeight: 600 }}>{rentingDetails.contractYears.toFixed(2)} años</span>
                </div>
                <div style={{ fontSize: 11, color: TEXT_2, display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                  <span>Financiado con interés (5%)</span>
                  <span style={{ fontWeight: 600 }}>{fmtAlways(round2(rentingDetails.financedWithInterest))}</span>
                </div>
                <div style={{ fontSize: 11, color: TEXT_2, display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                  <span>Cuota adicional compra activo</span>
                  <span style={{ fontWeight: 600 }}>{fmtAlways(round2(rentingDetails.finalPurchaseQuota))}</span>
                </div>
                <div style={{ fontSize: 11, color: TEXT_2, display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                  <span>Coste total CaixaBank</span>
                  <span style={{ fontWeight: 600 }}>{fmtAlways(round2(rentingDetails.totalCaixabankCost))}</span>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', display: 'flex', justifyContent: 'space-between', marginTop: 5, borderTop: '1px solid #fde68a', paddingTop: 4 }}>
                  <span>Cuota mensual CaixaBank</span>
                  <span>{fmtAlways(round2(caixabankMonthlyFee))} /mes</span>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Semaphore + Results */}
          <div>
            {/* Semaphore */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 20, padding: '18px 24px',
              borderRadius: 10, marginBottom: 20,
              background: `linear-gradient(135deg, ${semColor}18 0%, ${semColor}08 100%)`,
              border: `2px solid ${semColor}`,
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%', background: semColor, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, color: '#fff', fontWeight: 700,
                boxShadow: `0 0 16px ${semColor}66`,
              }}>
                {semEmoji}
              </div>
              <div>
                <div style={{ fontSize: 11, color: TEXT_2, marginBottom: 2 }}>
                  {MODEL_NAMES[model]} —&nbsp;
                  {model === 'sale'          ? 'Margen bruto del activo'               :
                   model === 'rental'        ? 'Margen mensual sobre cuota cliente'    :
                                              'Margen mensual sobre mínimo garantizado'}
                </div>
                <div style={{ fontSize: 30, fontWeight: 700, color: semColor, lineHeight: 1 }}>
                  {mainMarginPct !== null ? fmtPctR(mainMarginPct) : '—'}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: semColor, marginTop: 4 }}>
                  {semLabel}
                </div>
              </div>
            </div>

            {/* ── VENTA results ── */}
            {model === 'sale' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {resultCard('Margen del Activo', <>
                  {row('Precio de venta activo', fmtAlways(assetSalePrice))}
                  {row('Coste interno activo', fmtAlways(assetPurchaseCost))}
                  {rowMargin('Margen activo', saleGrossMarginAmount, saleGrossMarginPct)}
                  <div style={{ margin: '8px 0', borderTop: `1px dashed ${BORDER}` }} />
                  {row('Ingresos personalización', fmtAlways(personalizationRevenue))}
                  {row('Coste personalización', fmtAlways(personalizationCostTotal))}
                  {rowMargin('Margen personalización', personalizationMarginAmount, personalizationMarginPct)}
                  <div style={{ margin: '8px 0', borderTop: `2px solid ${BORDER}` }} />
                  {rowMargin('Margen bruto total', totalGrossMarginAmount, totalGrossMarginPct, true)}
                </>)}

                {resultCard('Margen OPEX', <>
                  {row('Ingresos licencias /año', fmtAlways(softwareRevYear))}
                  {row('Coste licencias interno /año', fmtAlways(annualLicenseCost))}
                  {rowMargin('Margen licencias', licenseMarginAmount, licenseMarginPct)}
                  <div style={{ margin: '6px 0' }} />
                  {row('Ingresos mantenimiento /año', fmtAlways(maintRevYear))}
                  {row('Coste mantenimiento interno /año', fmtAlways(annualMaintCost))}
                  {rowMargin('Margen mantenimiento', maintMarginAmount, maintMarginPct)}
                  <div style={{ margin: '6px 0' }} />
                  {row('Ingresos OPEX total /año', fmtAlways(annualOpexRevenue))}
                  {rowMargin('Margen OPEX total /año', annualOpexMargin, annualOpexMarginPct, true)}
                </>)}
              </div>
            )}

            {/* ── ALQUILER results ── */}
            {model === 'rental' && canShowRentalResults && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {resultCard('Margen Mensual', <>
                  {row('Cuota cliente', fmtAlways(signedMonthlyFee) + ' /mes')}
                  {row('Cuota CaixaBank', fmtAlways(round2(caixabankMonthlyFee)) + ' /mes')}
                  {row('OPEX mensual', fmtAlways(round2(monthlyOpexCost)) + ' /mes')}
                  {rowMargin('Margen mensual', monthlyRentalMargin, monthlyRentalMarginPct, true)}
                </>)}

                {resultCard(`Total Contrato (${contractMonths} meses)`, <>
                  {row('Ingresos totales', fmtAlways(round2(totalRentalRevenue)))}
                  {row('Costes totales', fmtAlways(round2(totalRentalCost)))}
                  {rowMargin('Margen total contrato', totalRentalMargin, totalRentalMarginPct, true)}
                </>)}
              </div>
            )}

            {/* ── REVENUE-SHARE results ── */}
            {model === 'revenue_share' && canShowRentalResults && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {resultCard('Margen Mensual Garantizado', <>
                  {row('Cláusula mínima anual', fmtAlways(minAnnualGuarantee) + ' /año')}
                  {row('Cláusula mínima mensual', fmtAlways(round2(minimumMonthlyGuarantee)) + ' /mes')}
                  {row('Cuota CaixaBank', fmtAlways(round2(caixabankMonthlyFee)) + ' /mes')}
                  {row('OPEX mensual', fmtAlways(round2(monthlyOpexCost)) + ' /mes')}
                  {rowMargin('Margen mensual garantizado', monthlyRSMargin, monthlyRSMarginPct, true)}
                </>)}

                {resultCard(`Total Contrato (${contractMonths} meses)`, <>
                  {row('Ingresos mínimos contrato', fmtAlways(round2(totalRSRevenue)))}
                  {row('Costes contrato', fmtAlways(round2(totalRSCost)))}
                  {rowMargin('Margen total contrato', totalRSMargin, totalRSMarginPct, true)}
                </>)}
              </div>
            )}

            {/* ── Financial Analysis cards (rental / revenue-share) ── */}
            {(model === 'rental' || model === 'revenue_share') && canShowRentalResults && (() => {
              const contractYears = contractMonths / 12;
              const metricBox = (bg, border, title, value, valueColor, sub1, sub2) => (
                <div style={{ background: bg, borderRadius: 8, padding: '14px 16px', border: `1px solid ${border}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: valueColor, lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
                  <div style={{ fontSize: 10, color: TEXT_2 }}>{sub1}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: valueColor, marginTop: 3 }}>{sub2}</div>
                </div>
              );
              const finCard = (title, subtitle, inv, invLabel, { van, tirAnnual, paybackYears }) => (
                <div style={{ marginTop: 16, background: '#fafbfc', borderRadius: 8, padding: '14px 16px', border: `1px solid ${BORDER}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: TEAL, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
                    <div style={{ fontSize: 10, color: TEXT_2 }}>{subtitle} &mdash; <strong>{inv !== null ? `${Math.round(inv).toLocaleString('es-ES')} €` : '—'}</strong> {invLabel}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {metricBox(
                      van !== null && van >= 0 ? '#f0fdf4' : '#fef2f2',
                      van !== null && van >= 0 ? '#86efac' : '#fca5a5',
                      'VAN — Valor Actual Neto',
                      van !== null ? `${van.toLocaleString('es-ES')} €` : '—',
                      van !== null ? (van >= 0 ? GREEN : RED) : TEXT_3,
                      `Tasa de descuento: ${(ANNUAL_INTEREST_RATE * 100).toFixed(0)}%`,
                      van !== null ? (van >= 0 ? '✓ Proyecto crea valor' : '✗ Proyecto destruye valor') : '—'
                    )}
                    {metricBox(
                      tirAnnual !== null && tirAnnual >= ANNUAL_INTEREST_RATE ? '#f0fdf4' : '#fef2f2',
                      tirAnnual !== null && tirAnnual >= ANNUAL_INTEREST_RATE ? '#86efac' : '#fca5a5',
                      'TIR — Tasa Interna de Rentabilidad',
                      tirAnnual !== null ? fmtPctR(tirAnnual) : '—',
                      tirAnnual !== null ? (tirAnnual >= ANNUAL_INTEREST_RATE ? GREEN : RED) : TEXT_3,
                      `Umbral mínimo (WACC): ${(ANNUAL_INTEREST_RATE * 100).toFixed(0)}%`,
                      tirAnnual !== null ? (tirAnnual >= ANNUAL_INTEREST_RATE ? '✓ Por encima del umbral' : '✗ Por debajo del umbral') : 'Sin flujo positivo'
                    )}
                    {metricBox(
                      paybackYears !== null && paybackYears <= contractYears ? '#f0fdf4' : paybackYears !== null ? '#fffbeb' : '#fef2f2',
                      paybackYears !== null && paybackYears <= contractYears ? '#86efac' : paybackYears !== null ? '#fde68a' : '#fca5a5',
                      'Payback — Período de Recuperación',
                      paybackYears !== null ? `${paybackYears.toFixed(1)} años` : '> contrato',
                      paybackYears !== null ? (paybackYears <= contractYears ? GREEN : AMBER) : RED,
                      `Duración contrato: ${contractYears.toFixed(1)} años`,
                      paybackYears !== null ? (paybackYears <= contractYears ? '✓ Recuperado dentro del contrato' : '⚠ No recuperado en el contrato') : '✗ Sin margen positivo'
                    )}
                  </div>
                </div>
              );
              return (
                <>
                  {assetPurchaseCost > 0 && finCard(
                    'Análisis Financiero — Coste del Activo',
                    'Inversión de referencia',
                    assetPurchaseCost,
                    '(coste activo)',
                    fin1
                  )}
                  {caixabankTotalCost > 0 && finCard(
                    'Análisis Financiero — Financiación CaixaBank',
                    'Inversión de referencia',
                    caixabankTotalCost,
                    '(total CaixaBank)',
                    fin2
                  )}
                </>
              );
            })()}

            {(model === 'rental' || model === 'revenue_share') && !canShowRentalResults && (
              <div style={{ padding: 24, textAlign: 'center', color: TEXT_3, fontSize: 13, fontStyle: 'italic', background: '#fafbfc', borderRadius: 8, border: `1px solid ${BORDER}` }}>
                Introduce la duración del contrato para ver los resultados.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Main ──────────────────────────────────────
  // ─── Render: Product Picker Modal ─────────────────────
  renderAddProductModal() {
    const { addProductModal, addProductModalSearch,
            cabinetItems, posItems, spareItems, consumableItems,
            installationType, saleMode } = this.state;
    if (!addProductModal) return null;

    const isExt   = installationType === 'exterior';
    const showSale = saleMode === 'purchase';
    const showRent = saleMode === 'rental' || saleMode === 'revenue_share';
    const search   = (addProductModalSearch || '').toLowerCase();

    let title, rawProducts, addedCodes, onAdd;

    if (addProductModal === 'consumible') {
      title       = 'Añadir Consumible';
      rawProducts = this.getProducts('consumible');
      addedCodes  = new Set(consumableItems.map(it => it.code));
      onAdd       = (p) => this.addConsumableDirect(p);
    } else {
      const catLabel = addProductModal === 'cabinet' ? 'Armario de Taquillas'
                     : addProductModal === 'pos'     ? 'Punto de Venta'
                     : 'Recambio';
      title = `Añadir ${catLabel}`;

      const catApiName = addProductModal === 'spare' ? 'recambio' : addProductModal;
      rawProducts = this.getProducts(catApiName).filter(p => {
        if (addProductModal === 'spare') return true; // recambios have no env restriction
        if (p.env === 'indoor'   && isExt)  return false;
        if (p.env === 'outdoor'  && !isExt) return false;
        return true;
      });

      const currentItems = addProductModal === 'cabinet' ? cabinetItems
                         : addProductModal === 'pos'     ? posItems
                         : spareItems;

      if (addProductModal === 'pos') {
        const hasQR    = currentItems.some(it => it.code === 'POS_QR');
        const hasNonQR = currentItems.some(it => it.code !== 'POS_QR');
        rawProducts = rawProducts.filter(p => {
          if (hasQR    && p.code !== 'POS_QR') return false;
          if (hasNonQR && p.code === 'POS_QR') return false;
          return true;
        });
      }

      addedCodes = new Set(currentItems.map(it => it.code));
      onAdd      = (p) => this.addItemDirect(addProductModal, p);
    }

    const available = rawProducts.filter(p => !addedCodes.has(p.code));
    const filtered  = search
      ? available.filter(p =>
          (p.name || '').toLowerCase().includes(search) ||
          (p.code || '').toLowerCase().includes(search))
      : available;

    const close = () => this.setState({ addProductModal: null, addProductModalSearch: '' });

    const thM = {
      padding: '10px 16px', textAlign: 'right', fontSize: 11, fontWeight: 600,
      color: TH_COLOR, background: TH_BG, textTransform: 'uppercase', letterSpacing: 0.5,
      borderBottom: `2px solid ${BORDER}`, whiteSpace: 'nowrap',
    };

    return (
      <div
        onClick={(e) => e.target === e.currentTarget && close()}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,10,30,0.55)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}
      >
        <div style={{
          background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          width: '100%', maxWidth: 680, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Modal header */}
          <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: DARK }}>{title}</div>
            <button onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: TEXT_2, padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center' }}>
              <X size={20} />
            </button>
          </div>

          {/* Search */}
          <div style={{ padding: '12px 24px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
            <input
              type="text"
              placeholder="Buscar por nombre o código..."
              value={addProductModalSearch || ''}
              onChange={(e) => this.setState({ addProductModalSearch: e.target.value })}
              autoFocus
              style={{ ...inputStyle, margin: 0 }}
            />
          </div>

          {/* Product list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '40px 24px', textAlign: 'center', color: TEXT_3, fontSize: 14 }}>
                {available.length === 0
                  ? 'No hay más productos disponibles'
                  : 'Sin resultados para la búsqueda'}
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thM, textAlign: 'left', padding: '10px 24px' }}>Producto</th>
                    {addProductModal !== 'consumible' && (
                      <th style={thM}>Taquillas</th>
                    )}
                    <th style={thM}>
                      {addProductModal === 'consumible'
                        ? 'Precio'
                        : showSale
                        ? (isExt ? 'Precio Ext.' : 'Precio Int.')
                        : (isExt ? 'Alq. Ext.' : 'Alq. Int.')}
                    </th>
                    <th style={{ ...thM, padding: '10px 24px 10px 12px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => {
                    const price = addProductModal === 'consumible'
                      ? (p.priceInterior || 0)
                      : showSale
                      ? (isExt ? (p.priceExterior || 0) : (p.priceInterior || 0))
                      : (isExt ? (p.rentExterior || 0) : (p.rentInterior || 0));
                    const imgKey = p.image || '';
                    const imgSrc = !imgKey ? null
                      : imgKey === 'qr' ? '/images/configurator/qr.png'
                      : (imgKey.startsWith('key_') || imgKey.startsWith('lock_') || imgKey === 'final') ? `/images/configurator/${imgKey}.png`
                      : `/images/configurator/${isExt ? 'exterior' : 'interior'}_${imgKey}.svg`;
                    return (
                      <tr key={p.code} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc', borderBottom: `1px solid ${BORDER}` }}>
                        <td style={{ padding: '10px 24px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {imgSrc ? (
                              <img src={imgSrc} alt={p.name}
                                onError={(e) => { if (!e.target.src.includes('exterior_')) { e.target.src = `/images/configurator/exterior_${imgKey}.svg`; } else { e.target.style.display = 'none'; } }}
                                style={{ width: 44, height: 44, objectFit: 'contain', flexShrink: 0 }} />
                            ) : (
                              <div style={{ width: 44, height: 44, flexShrink: 0 }} />
                            )}
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: DARK }}>{p.name}</div>
                              <div style={{ fontSize: 11, color: TEXT_3, marginTop: 2 }}>{p.code}</div>
                            </div>
                          </div>
                        </td>
                        {addProductModal !== 'consumible' && (
                          <td style={{ padding: '12px', textAlign: 'right', fontSize: 13, color: TEXT_2, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            {(p.lockers || 0) > 0
                              ? <><strong style={{ color: TEAL }}>{p.lockers}</strong> taq.</>
                              : '—'}
                          </td>
                        )}
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: TEXT_1, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {fmtAlways(price)}{showRent && addProductModal !== 'consumible' ? ' /mes' : ''}
                        </td>
                        <td style={{ padding: '12px 24px 12px 12px', textAlign: 'right', verticalAlign: 'middle' }}>
                          <button
                            onClick={() => onAdd(p)}
                            style={{ background: 'linear-gradient(135deg, #022040 0%, #0a4080 100%)', border: 'none', color: '#fff', padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                            <Plus size={13} /> Añadir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 24px', borderTop: `1px solid ${BORDER}`, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={close}
              style={{ background: '#f1f5f9', border: 'none', color: TEXT_2, padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { products } = this.props;
    if (!products) {
      return <div style={{ padding: 60, textAlign: 'center', color: TEXT_2 }}>Cargando catálogo...</div>;
    }

    const totals = this.calculateTotals();
    const cabinets = this.getProducts('cabinet');
    const posProducts = this.getProducts('pos');

    return (
      <div style={{ fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", background: BG, minHeight: '100vh', padding: '24px 24px 60px' }}>
        {this.renderHero()}

        {this.state.showPreview ? (
          <div key="preview" style={{ margin: '0 auto' }}>
            <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${BORDER}`, padding: '30px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              {this.renderPrintContent(totals)}
            </div>
          </div>
        ) : (
          <div key="editor">
            {this.renderSummaryBanner(totals)}
            {this.renderInputSection()}
            {this.renderVisualSection()}
            {['5b03210d8aca3af35664d0e2', '5cf612434cffd40ee39fcc2c'].includes(this.props.authId) &&
              this.renderRentabilityBlock(totals)}
          </div>
        )}

        {this.renderAddProductModal()}

        {/* Hidden print container for PDF export — two separate pages.
            Page 1 is wide (2800px) so the visual module layout keeps its
            fidelity. Page 2 is narrower (1400px) so the fixed px-based font
            sizes translate to larger, readable text on A4 landscape. */}
        <div style={{ position: 'fixed', left: 0, top: 0, zIndex: -9999, opacity: 0, pointerEvents: 'none' }}>
          <div ref={this.printCoverRef} style={{ width: 720, background: '#fff', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintCoverPage()}
          </div>
          <div ref={this.printVisualRef} style={{ width: 2800, background: '#fff', padding: '60px 80px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintVisualPage()}
          </div>
          <div ref={this.printDataRef} style={{ width: 720, background: '#fff', padding: '28px 32px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintDataPage(totals)}
          </div>
          <div ref={this.printOfertaRef} style={{ width: 720, background: '#fff', padding: '28px 32px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderPrintOfertaPage(totals, this.state.moduleMode === 'list')}
          </div>
          <div ref={this.printRsStudyRef} style={{ width: 720, background: '#fff', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
            {this.renderRsStudyPage()}
          </div>
        </div>
      </div>
    );
  }
}

// ─── Redux Connection ────────────────────────────────────
const mapStateToProps = (state) => ({
  role:     state.auth.role,
  type:     state.auth.type,
  fullname: state.auth.fullname,
  authId:   state.auth.id,
  products: state.proposalCatalog.products,
  settings: state.proposalCatalog.settings,
  loading:  state.proposalCatalog.loading,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchProposalProducts,
  fetchProposalSettings,
  seedProposalCatalog,
  createProposal,
  updateProposal,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ProposalConfiguratorView);
